import React, { useState, useEffect, useRef } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import {
  onAuthChange,
  getUserData,
  savePortfolios,
  canAddPortfolio,
  updateUserData,
  resendVerificationEmail,
} from "./firebase-auth";
import { checkPaymentStatus, handlePaymentSuccess } from "./stripe-checkout";
import {
  LoginModal,
  SignUpModal,
  UserProfile,
  UpgradePrompt,
  authStyles,
} from "./AuthComponents";

const RebalancingAssistant = () => {
  // Refs
  const addFormRef = useRef(null);
  const symbolInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const classSelectRef = useRef(null);
  const isInitialLoad = useRef(true); // Track if this is first auth check

  // Auth state (ADD FIRST - before other state)
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Existing state
  const [portfolio, setPortfolio] = useState([]);
  const [portfolios, setPortfolios] = useState([]); // Array of {name, holdings, id}
  const [currentPortfolioId, setCurrentPortfolioId] = useState(null);
  const [portfolioName, setPortfolioName] = useState("");
  const [editingNameId, setEditingNameId] = useState(null);
  const [showAggregated, setShowAggregated] = useState(false);
  const [uploadMode, setUploadMode] = useState("new"); // 'new' or 'update'
  const [appendMode, setAppendMode] = useState(false); // When true, add to existing instead of replace
  const [allocationPreset, setAllocationPreset] = useState("moderate");
  const [newMoneyAmount, setNewMoneyAmount] = useState(1000);
  const [targetAllocation, setTargetAllocation] = useState({
    "US Stocks": 50,
    "International Stocks": 15,
    Bonds: 15,
    Crypto: 5,
    "Precious Metals": 5,
    "Real Estate": 5,
    Cash: 5,
  });
  // Save custom allocation separately so it's preserved when switching presets
  const [customAllocation, setCustomAllocation] = useState({
    "US Stocks": 50,
    "International Stocks": 15,
    Bonds: 15,
    Crypto: 5,
    "Precious Metals": 5,
    "Real Estate": 5,
    Cash: 5,
  });
  const [rebalanceThreshold, setRebalanceThreshold] = useState(5);
  const [view, setView] = useState("upload"); // upload, portfolio, rebalance
  const [csvError, setCsvError] = useState("");
  const [unknownTickers, setUnknownTickers] = useState([]); // Tickers needing classification
  const [showClassifyModal, setShowClassifyModal] = useState(false);
  const [customAssetMappings, setCustomAssetMappings] = useState({}); // User's custom mappings
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [editingHolding, setEditingHolding] = useState(null); // Track which holding is being edited (by symbol)
  const [manualUploadMode, setManualUploadMode] = useState(false); // CSV vs Manual entry

  // Preset allocation strategies
  const allocationPresets = {
    aggressive: {
      "US Stocks": 55,
      "International Stocks": 20,
      Bonds: 5,
      Crypto: 8,
      "Precious Metals": 5,
      "Real Estate": 5,
      Cash: 2,
    },
    moderate: {
      "US Stocks": 50,
      "International Stocks": 15,
      Bonds: 15,
      Crypto: 5,
      "Precious Metals": 5,
      "Real Estate": 5,
      Cash: 5,
    },
    conservative: {
      "US Stocks": 30,
      "International Stocks": 10,
      Bonds: 35,
      Crypto: 2,
      "Precious Metals": 8,
      "Real Estate": 10,
      Cash: 5,
    },
  };

  const applyPreset = (presetName) => {
    if (presetName === "custom") {
      setAllocationPreset("custom");
      // Restore the saved custom allocation
      setTargetAllocation({ ...customAllocation });
      return;
    }
    setAllocationPreset(presetName);
    setTargetAllocation({ ...allocationPresets[presetName] });
  };

  // Auth state listener - Load user data and portfolios from Firebase
  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // Reload user to get latest emailVerified status
        await firebaseUser.reload();
        setUser(firebaseUser);

        // Load user data from Firestore
        const { data } = await getUserData(firebaseUser.uid);
        setUserData(data);

        // Load portfolios from Firestore
        if (data?.portfolios && data.portfolios.length > 0) {
          setPortfolios(data.portfolios);
          setCurrentPortfolioId(data.portfolios[0].id);
          setPortfolio(data.portfolios[0].holdings);
          setPortfolioName(data.portfolios[0].name);
          setView("portfolio");
        }
      } else {
        setUser(null);
        setUserData(null);
        // Don't auto-show login modal - let users browse as guest
        // Login modal will show when they click "Sign In" or try to upload
      }
      setAuthLoading(false);
      isInitialLoad.current = false; // After first check, no longer initial load
    });

    return () => unsubscribe();
  }, []);

  // Check email verification status periodically (but not forever)
  useEffect(() => {
    if (!user || user.emailVerified) return;

    let checkCount = 0;
    const maxChecks = 60; // Check for max 5 minutes (60 checks × 5 seconds)

    // Check every 5 seconds if user has verified their email
    const interval = setInterval(async () => {
      checkCount++;
      
      try {
        await user.reload();
        if (user.emailVerified) {
          // Force re-render with updated user
          setUser({ ...user });
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }

      // Stop checking after 5 minutes to avoid unnecessary requests
      if (checkCount >= maxChecks) {
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user]);

  // Auto-save portfolios to Firebase whenever they change
  useEffect(() => {
    if (user && portfolios.length > 0 && !authLoading) {
      savePortfolios(user.uid, portfolios);
    }
  }, [portfolios, user, authLoading]);

  // Load custom asset class mappings from Firebase
  useEffect(() => {
    const loadCustomMappings = async () => {
      if (user) {
        const { data } = await getUserData(user.uid);
        if (data?.customAssetMappings) {
          setCustomAssetMappings(data.customAssetMappings);
        }
      }
    };
    loadCustomMappings();
  }, [user]);

  // Check for payment success on app load
  useEffect(() => {
    const checkPayment = async () => {
      const { success, sessionId } = checkPaymentStatus();

      if (success && sessionId && user) {
        // Payment successful! Update user to premium
        const upgraded = await handlePaymentSuccess(sessionId, async (tier) => {
          await updateUserData(user.uid, { tier });
          setUserData({ ...userData, tier });
        });

        if (upgraded) {
          alert("🎉 Welcome to Premium! You now have unlimited portfolios.");
          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      }
    };

    if (user) {
      checkPayment();
    }
  }, [user]);

  // Calculate total allocation percentage
  const getTotalAllocation = () => {
    return Object.values(targetAllocation).reduce((sum, val) => sum + val, 0);
  };

  // Get current portfolio (either single or aggregated)
  const getCurrentPortfolio = () => {
    if (showAggregated && portfolios.length > 0) {
      // Aggregate all portfolios
      const aggregated = [];
      portfolios.forEach((p) => {
        p.holdings.forEach((holding) => {
          const existing = aggregated.find((h) => h.symbol === holding.symbol);
          if (existing) {
            existing.quantity += holding.quantity;
            existing.marketValue += holding.marketValue;
            existing.costBasis += holding.costBasis;
          } else {
            aggregated.push({ ...holding });
          }
        });
      });
      return aggregated;
    }
    return portfolio;
  };

  const switchPortfolio = (portfolioId) => {
    const selectedPortfolio = portfolios.find((p) => p.id === portfolioId);
    if (selectedPortfolio) {
      setCurrentPortfolioId(portfolioId);
      setPortfolio(selectedPortfolio.holdings);
      setPortfolioName(selectedPortfolio.name);
      setShowAggregated(false);
    }
  };

  const viewAggregated = () => {
    // Check if user has premium tier for aggregated view
    if (userData?.tier !== "premium") {
      setShowUpgrade(true);
      return;
    }
    setShowAggregated(true);
    setPortfolio(getCurrentPortfolio());
  };

  // Asset class explanations for tooltips
  const assetClassExplanations = {
    "US Stocks":
      "Primary growth engine of your portfolio. US companies offer strong returns, innovation, and liquidity. Historically ~10% annual returns over long periods.",
    "International Stocks":
      "Diversification beyond US markets. Exposure to global growth, different economic cycles, and currency diversification. Reduces US-specific risk.",
    Bonds:
      "Stability and income. Bonds provide steady cash flow, lower volatility than stocks, and tend to rise when stocks fall. Essential for capital preservation.",
    Crypto:
      "High-risk, high-reward speculation. Digital assets offer potential asymmetric returns but extreme volatility. Only allocate what you can afford to lose.",
    "Precious Metals":
      'Inflation hedge and crisis insurance. Gold/silver preserve purchasing power during inflation, currency devaluation, or market crashes. Portfolio "insurance."',
    "Real Estate":
      "Income generation and inflation protection. REITs provide exposure to property markets, steady dividends, and historically low correlation to stocks.",
    Cash: "Liquidity and opportunity fund. Cash provides safety, emergency reserves, and dry powder to buy during market crashes. Helps you sleep at night.",
  };

  // Historical correlation data (simplified - based on long-term averages)
  const correlationMatrix = {
    "US Stocks": {
      "US Stocks": 1.0,
      "International Stocks": 0.85,
      Bonds: -0.1,
      Crypto: 0.45,
      "Precious Metals": 0.15,
      "Real Estate": 0.7,
      Cash: 0.0,
    },
    "International Stocks": {
      "US Stocks": 0.85,
      "International Stocks": 1.0,
      Bonds: -0.05,
      Crypto: 0.4,
      "Precious Metals": 0.2,
      "Real Estate": 0.65,
      Cash: 0.0,
    },
    Bonds: {
      "US Stocks": -0.1,
      "International Stocks": -0.05,
      Bonds: 1.0,
      Crypto: -0.2,
      "Precious Metals": 0.1,
      "Real Estate": 0.05,
      Cash: 0.2,
    },
    Crypto: {
      "US Stocks": 0.45,
      "International Stocks": 0.4,
      Bonds: -0.2,
      Crypto: 1.0,
      "Precious Metals": 0.25,
      "Real Estate": 0.35,
      Cash: 0.0,
    },
    "Precious Metals": {
      "US Stocks": 0.15,
      "International Stocks": 0.2,
      Bonds: 0.1,
      Crypto: 0.25,
      "Precious Metals": 1.0,
      "Real Estate": 0.3,
      Cash: 0.0,
    },
    "Real Estate": {
      "US Stocks": 0.7,
      "International Stocks": 0.65,
      Bonds: 0.05,
      Crypto: 0.35,
      "Precious Metals": 0.3,
      "Real Estate": 1.0,
      Cash: 0.0,
    },
    Cash: {
      "US Stocks": 0.0,
      "International Stocks": 0.0,
      Bonds: 0.2,
      Crypto: 0.0,
      "Precious Metals": 0.0,
      "Real Estate": 0.0,
      Cash: 1.0,
    },
  };

  // Calculate portfolio diversification score
  const calculateDiversificationScore = () => {
    const currentAlloc = calculateCurrentAllocation();

    if (currentAlloc.length === 0) return null;

    let totalWeightedCorrelation = 0;
    let totalWeight = 0;

    // Calculate weighted average correlation
    for (let i = 0; i < currentAlloc.length; i++) {
      for (let j = i + 1; j < currentAlloc.length; j++) {
        const asset1 = currentAlloc[i];
        const asset2 = currentAlloc[j];
        const weight1 = asset1.percentage / 100;
        const weight2 = asset2.percentage / 100;
        const correlation = correlationMatrix[asset1.name]?.[asset2.name] || 0;

        const pairWeight = weight1 * weight2;
        totalWeightedCorrelation += correlation * pairWeight;
        totalWeight += pairWeight;
      }
    }

    const avgCorrelation =
      totalWeight > 0 ? totalWeightedCorrelation / totalWeight : 0;

    // Calculate percentage of portfolio in highly correlated assets (stocks + RE + crypto)
    const highlyCorrelated = currentAlloc
      .filter((a) =>
        ["US Stocks", "International Stocks", "Real Estate", "Crypto"].includes(
          a.name
        )
      )
      .reduce((sum, a) => sum + a.percentage, 0);

    // Identify key diversifiers (negative or low correlation to stocks)
    const diversifiers = currentAlloc
      .filter((a) => ["Bonds", "Precious Metals", "Cash"].includes(a.name))
      .reduce((sum, a) => sum + a.percentage, 0);

    return {
      score: avgCorrelation,
      rating:
        avgCorrelation < 0.3
          ? "Excellent"
          : avgCorrelation < 0.5
          ? "Good"
          : avgCorrelation < 0.7
          ? "Moderate"
          : "Poor",
      highlyCorrelated,
      diversifiers,
      recommendations: [],
    };
  };

  // Expected returns and volatility (historical averages)
  const assetClassStats = {
    "US Stocks": { return: 10.0, volatility: 18.0 },
    "International Stocks": { return: 8.5, volatility: 20.0 },
    Bonds: { return: 4.5, volatility: 6.0 },
    Crypto: { return: 15.0, volatility: 80.0 },
    "Precious Metals": { return: 5.0, volatility: 16.0 },
    "Real Estate": { return: 9.0, volatility: 20.0 },
    Cash: { return: 2.5, volatility: 1.0 },
  };

  const runMonteCarloSimulation = (initialValue, maxYears = 40, simulations = 1000) => {
    const currentAlloc = calculateCurrentAllocation();
    const yearlyResults = {}; // Store results for each year
    
    // Initialize arrays for each year (1 to maxYears)
    for (let y = 1; y <= maxYears; y++) {
      yearlyResults[y] = [];
    }

    // Run all simulations once
    for (let sim = 0; sim < simulations; sim++) {
      let value = initialValue;

      for (let year = 1; year <= maxYears; year++) {
        let portfolioReturn = 0;

        currentAlloc.forEach((asset) => {
          const stats = assetClassStats[asset.name];
          if (stats) {
            const weight = asset.percentage / 100;
            // Generate random return using normal distribution approximation
            const randomReturn =
              stats.return + stats.volatility * (Math.random() - 0.5) * 2;
            portfolioReturn += weight * randomReturn;
          }
        });

        value = value * (1 + portfolioReturn / 100);
        
        // Store this simulation's value at this year
        yearlyResults[year].push(value);
      }
    }

    // Sort results for each year
    for (let y = 1; y <= maxYears; y++) {
      yearlyResults[y].sort((a, b) => a - b);
    }

    // Return function to get stats for any year
    return {
      getStatsForYear: (year) => {
        const results = yearlyResults[year] || [];
        if (results.length === 0) return null;
        
        return {
          median: results[Math.floor(simulations * 0.5)],
          percentile10: results[Math.floor(simulations * 0.1)],
          percentile25: results[Math.floor(simulations * 0.25)],
          percentile75: results[Math.floor(simulations * 0.75)],
          percentile90: results[Math.floor(simulations * 0.9)],
          min: results[0],
          max: results[results.length - 1],
          allResults: results,
        };
      },
      yearlyResults, // Also expose raw data
    };
  };

  // Asset class mapping
  const assetClassMap = {
    // US Stocks - Mega Cap Tech
    AAPL: "US Stocks",
    MSFT: "US Stocks",
    GOOGL: "US Stocks",
    GOOG: "US Stocks",
    AMZN: "US Stocks",
    NVDA: "US Stocks",
    META: "US Stocks",
    TSLA: "US Stocks",
    "BRK.B": "US Stocks",
    "BRK.A": "US Stocks",
    V: "US Stocks",
    MA: "US Stocks",
    JPM: "US Stocks",
    JNJ: "US Stocks",
    WMT: "US Stocks",
    PG: "US Stocks",
    XOM: "US Stocks",
    CVX: "US Stocks",
    LLY: "US Stocks",
    UNH: "US Stocks",
    HD: "US Stocks",
    BAC: "US Stocks",
    ABBV: "US Stocks",
    ORCL: "US Stocks",
    AVGO: "US Stocks",
    COST: "US Stocks",
    PEP: "US Stocks",
    KO: "US Stocks",
    ADBE: "US Stocks",
    CRM: "US Stocks",
    MRK: "US Stocks",
    TMO: "US Stocks",
    CSCO: "US Stocks",
    ACN: "US Stocks",
    ABT: "US Stocks",
    NKE: "US Stocks",
    TXN: "US Stocks",
    DHR: "US Stocks",
    DIS: "US Stocks",
    WFC: "US Stocks",
    NFLX: "US Stocks",
    VZ: "US Stocks",
    INTC: "US Stocks",
    CMCSA: "US Stocks",
    AMD: "US Stocks",
    T: "US Stocks",
    PM: "US Stocks",
    PFE: "US Stocks",
    UPS: "US Stocks",
    LOW: "US Stocks",
    IBM: "US Stocks",
    GE: "US Stocks",
    BA: "US Stocks",
    CAT: "US Stocks",
    MMM: "US Stocks",
    HON: "US Stocks",
    PYPL: "US Stocks",
    SHOP: "US Stocks",
    SQ: "US Stocks",
    COIN: "US Stocks",
    HOOD: "US Stocks",
    UBER: "US Stocks",
    LYFT: "US Stocks",
    ABNB: "US Stocks",
    RBLX: "US Stocks",
    ROKU: "US Stocks",
    SNAP: "US Stocks",
    PINS: "US Stocks",

    // US Stock ETFs
    VTI: "US Stocks",
    VOO: "US Stocks",
    SPY: "US Stocks",
    QQQ: "US Stocks",
    VTV: "US Stocks",
    VUG: "US Stocks",
    IWM: "US Stocks",
    DIA: "US Stocks",
    ITOT: "US Stocks",
    SCHB: "US Stocks",
    VB: "US Stocks",
    VXF: "US Stocks",
    IVV: "US Stocks",
    IWF: "US Stocks",
    IWD: "US Stocks",
    IJH: "US Stocks",
    IJR: "US Stocks",
    MDY: "US Stocks",
    IWV: "US Stocks",
    SCHX: "US Stocks",
    VIG: "US Stocks",
    VONG: "US Stocks",
    VONV: "US Stocks",
    SCHG: "US Stocks",
    SCHV: "US Stocks",
    VYM: "US Stocks",
    DVY: "US Stocks",
    DGRO: "US Stocks",

    // International Stocks - Individual
    TSM: "International Stocks",
    ASML: "International Stocks",
    NVO: "International Stocks",
    TM: "International Stocks",
    BABA: "International Stocks",
    SAP: "International Stocks",
    UL: "International Stocks",
    SONY: "International Stocks",
    NVS: "International Stocks",
    HSBC: "International Stocks",
    BP: "International Stocks",
    SHEL: "International Stocks",

    // International Stock ETFs
    VEA: "International Stocks",
    VWO: "International Stocks",
    VXUS: "International Stocks",
    EFA: "International Stocks",
    IEMG: "International Stocks",
    IXUS: "International Stocks",
    VT: "International Stocks",
    ACWI: "International Stocks",
    VIGI: "International Stocks",
    IEFA: "International Stocks",
    IDEV: "International Stocks",
    EEM: "International Stocks",
    VGK: "International Stocks",
    EWJ: "International Stocks",
    FXI: "International Stocks",
    INDA: "International Stocks",
    EWZ: "International Stocks",
    SCHA: "International Stocks",
    VYMI: "International Stocks",
    SCHF: "International Stocks",
    VSS: "International Stocks",

    // Bond ETFs
    BND: "Bonds",
    AGG: "Bonds",
    TLT: "Bonds",
    VGIT: "Bonds",
    LQD: "Bonds",
    HYG: "Bonds",
    TIP: "Bonds",
    MUB: "Bonds",
    VCIT: "Bonds",
    VCSH: "Bonds",
    VGSH: "Bonds",
    VTIP: "Bonds",
    BIV: "Bonds",
    BSV: "Bonds",
    BLV: "Bonds",
    BNDX: "Bonds",
    SCHZ: "Bonds",
    SCHO: "Bonds",
    SCHR: "Bonds",
    GOVT: "Bonds",
    SHY: "Bonds",
    IEF: "Bonds",
    TLH: "Bonds",
    EDV: "Bonds",
    VGLT: "Bonds",
    JNK: "Bonds",
    SJNK: "Bonds",
    BKLN: "Bonds",
    EMB: "Bonds",
    EMLC: "Bonds",
    PCY: "Bonds",
    TOTL: "Bonds",

    // Crypto
    BTC: "Crypto",
    BITCOIN: "Crypto",
    ETH: "Crypto",
    ETHEREUM: "Crypto",
    GBTC: "Crypto",
    ETHE: "Crypto",
    BITO: "Crypto",
    BITI: "Crypto",
    BTCUSD: "Crypto",
    ETHUSD: "Crypto",
    "BTC-USD": "Crypto",
    "ETH-USD": "Crypto",

    // Precious Metals
    GLD: "Precious Metals",
    GOLD: "Precious Metals",
    IAU: "Precious Metals",
    SLV: "Precious Metals",
    SILVER: "Precious Metals",
    PSLV: "Precious Metals",
    PHYS: "Precious Metals",
    PPLT: "Precious Metals",
    PALL: "Precious Metals",
    GDX: "Precious Metals",
    GDXJ: "Precious Metals",
    BAR: "Precious Metals",
    OUNZ: "Precious Metals",
    SGOL: "Precious Metals",
    AAAU: "Precious Metals",

    // Real Estate
    VNQ: "Real Estate",
    SCHH: "Real Estate",
    IYR: "Real Estate",
    XLRE: "Real Estate",
    REIT: "Real Estate",
    RWR: "Real Estate",
    VNQI: "Real Estate",
    IFGL: "Real Estate",
    USRT: "Real Estate",
    REM: "Real Estate",
    MORT: "Real Estate",
    BBRE: "Real Estate",
    ICF: "Real Estate",
    FREL: "Real Estate",
    REZ: "Real Estate",

    // Cash / Money Market
    SHV: "Cash",
    VMFXX: "Cash",
    CASH: "Cash",
    VUSXX: "Cash",
    SGOV: "Cash",
    VCTXX: "Cash",
    BIL: "Cash",
    MINT: "Cash",
    JPST: "Cash",
    NEAR: "Cash",
    ICSH: "Cash",
    SPAXX: "Cash",
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      setCsvError("CSV file appears to be empty");
      return null;
    }

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

    // Find relevant columns
    const symbolCol = headers.findIndex(
      (h) => h.includes("symbol") || h.includes("ticker")
    );
    const qtyCol = headers.findIndex(
      (h) => h.includes("quantity") || h.includes("shares")
    );
    const priceCol = headers.findIndex(
      (h) => h.includes("price") || h.includes("last")
    );
    const valueCol = headers.findIndex(
      (h) => h.includes("value") || h.includes("market")
    );
    const costCol = headers.findIndex(
      (h) => h.includes("cost") || h.includes("basis")
    );

    if (symbolCol === -1) {
      setCsvError("Could not find Symbol/Ticker column");
      return null;
    }

    const holdings = [];
    const unknown = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      if (values.length < 2) continue;

      const symbol = values[symbolCol]?.toUpperCase();
      if (!symbol) continue;

      let quantity = qtyCol !== -1 ? parseFloat(values[qtyCol]) : 0;
      let price = priceCol !== -1 ? parseFloat(values[priceCol]) : 0;
      let marketValue =
        valueCol !== -1 ? parseFloat(values[valueCol]) : quantity * price;
      let costBasis = costCol !== -1 ? parseFloat(values[costCol]) : 0;

      // Validate: ensure no negative values
      quantity = Math.max(0, quantity || 0);
      price = Math.max(0, price || 0);
      marketValue = Math.max(0, marketValue || 0);
      costBasis = Math.max(0, costBasis || 0);

      if (marketValue > 0) {
        // Check: hardcoded map → custom mappings → mark as unknown
        let assetClass = assetClassMap[symbol] || customAssetMappings[symbol];

        if (!assetClass) {
          assetClass = "Other";
          if (!unknown.includes(symbol)) {
            unknown.push(symbol);
          }
        }

        holdings.push({
          symbol,
          quantity,
          price,
          marketValue,
          costBasis,
          assetClass,
        });
      }
    }

    if (holdings.length === 0) {
      setCsvError("No valid holdings found in CSV");
      return null;
    }

    // If there are unknown tickers, store them for classification
    if (unknown.length > 0) {
      setUnknownTickers(unknown);
    }

    return holdings;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Require authentication for uploads
    if (!user) {
      setShowLogin(true);
      e.target.value = ""; // Reset file input
      return;
    }

    setCsvError("");

    // Check if user can add portfolio (only for new uploads)
    if (uploadMode === "new" && user) {
      const { canAdd, message } = await canAddPortfolio(user.uid);
      if (!canAdd) {
        setShowUpgrade(true); // Show upgrade modal
        e.target.value = ""; // Reset file input
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const parsed = parseCSV(event.target.result);
      if (parsed) {
        if (uploadMode === "update" && currentPortfolioId) {
          // Update existing portfolio
          let finalHoldings;

          if (appendMode) {
            // Append mode: add new holdings to existing ones
            const existingPortfolio = portfolios.find(
              (p) => p.id === currentPortfolioId
            );
            const existingHoldings = existingPortfolio
              ? existingPortfolio.holdings
              : [];

            // Merge: update existing symbols, add new ones
            const holdingsMap = new Map();
            existingHoldings.forEach((h) => holdingsMap.set(h.symbol, h));
            parsed.forEach((h) => holdingsMap.set(h.symbol, h)); // New values overwrite if symbol exists

            finalHoldings = Array.from(holdingsMap.values());
          } else {
            // Replace mode: completely replace holdings
            finalHoldings = parsed;
          }

          const updatedPortfolios = portfolios.map((p) =>
            p.id === currentPortfolioId ? { ...p, holdings: finalHoldings } : p
          );
          setPortfolios(updatedPortfolios);
          setPortfolio(finalHoldings);

          // Show classification modal if needed
          if (unknownTickers.length > 0) {
            setShowClassifyModal(true);
          } else {
            setView("portfolio");
          }
        } else {
          // Create new portfolio
          const newPortfolioId = Date.now().toString();
          let defaultName = file.name.replace(".csv", "") || `Portfolio ${portfolios.length + 1}`;
          
          // Truncate long filenames to prevent layout issues
          if (defaultName.length > 30) {
            defaultName = defaultName.substring(0, 30) + "...";
          }

          setPortfolioName(defaultName);
          setPortfolio(parsed);

          const newPortfolio = {
            id: newPortfolioId,
            name: defaultName,
            holdings: parsed,
          };
          setPortfolios([...portfolios, newPortfolio]);
          setCurrentPortfolioId(newPortfolioId);
          setShowAggregated(false);

          // Show classification modal if needed
          if (unknownTickers.length > 0) {
            setShowClassifyModal(true);
          } else {
            setView("portfolio");
          }
        }
      }
    };
    reader.readAsText(file);
  };

  const updatePortfolioName = (portfolioId, newName) => {
    const updatedPortfolios = portfolios.map((p) =>
      p.id === portfolioId ? { ...p, name: newName } : p
    );
    setPortfolios(updatedPortfolios);
    if (portfolioId === currentPortfolioId) {
      setPortfolioName(newName);
    }
  };

  const deletePortfolio = (portfolioId) => {
    const updatedPortfolios = portfolios.filter((p) => p.id !== portfolioId);
    setPortfolios(updatedPortfolios);

    if (portfolioId === currentPortfolioId) {
      if (updatedPortfolios.length > 0) {
        switchPortfolio(updatedPortfolios[0].id);
      } else {
        setView("upload");
        setCurrentPortfolioId(null);
        setPortfolio([]);
      }
    }
  };

  // Save asset class classification
  const saveAssetClassification = async (symbol, assetClass) => {
    const newMappings = {
      ...customAssetMappings,
      [symbol]: assetClass,
    };
    setCustomAssetMappings(newMappings);

    // Save to Firebase
    if (user) {
      await updateUserData(user.uid, { customAssetMappings: newMappings });
    }
  };

  // Handle completing all classifications
  const handleClassifyComplete = async (classifications) => {
    // Save all classifications
    for (const [symbol, assetClass] of Object.entries(classifications)) {
      await saveAssetClassification(symbol, assetClass);
    }

    // Re-parse portfolio with new classifications
    const updatedHoldings = portfolio.map((holding) => {
      if (classifications[holding.symbol]) {
        return {
          ...holding,
          assetClass: classifications[holding.symbol],
        };
      }
      return holding;
    });

    setPortfolio(updatedHoldings);

    // Update in portfolios array
    const updatedPortfolios = portfolios.map((p) =>
      p.id === currentPortfolioId ? { ...p, holdings: updatedHoldings } : p
    );
    setPortfolios(updatedPortfolios);

    // Clear unknown tickers and close modal
    setUnknownTickers([]);
    setShowClassifyModal(false);
    setView("portfolio");
  };

  // Add holding manually
  const handleAddHolding = () => {
    const symbol = symbolInputRef.current?.value.trim().toUpperCase();
    let quantity = parseFloat(quantityInputRef.current?.value);
    let price = parseFloat(priceInputRef.current?.value);
    const assetClass = classSelectRef.current?.value || "US Stocks";

    // Validate: prevent negative values
    quantity = Math.max(0, quantity || 0);
    price = Math.max(0, price || 0);

    if (!symbol || !quantity || !price || quantity <= 0 || price <= 0) {
      alert("Please fill in Symbol, Shares (> 0), and Price (> 0)");
      return;
    }

    const marketValue = quantity * price;

    const holding = {
      symbol,
      quantity,
      price,
      marketValue,
      costBasis: 0,
      assetClass,
    };

    // Add to current portfolio
    const updatedHoldings = [...portfolio, holding];
    setPortfolio(updatedHoldings);

    // Update in portfolios array
    const updatedPortfolios = portfolios.map((p) =>
      p.id === currentPortfolioId ? { ...p, holdings: updatedHoldings } : p
    );
    setPortfolios(updatedPortfolios);

    // Reset form
    if (symbolInputRef.current) symbolInputRef.current.value = "";
    if (quantityInputRef.current) quantityInputRef.current.value = "";
    if (priceInputRef.current) priceInputRef.current.value = "";
    if (classSelectRef.current) classSelectRef.current.value = "US Stocks";

    setShowAddHolding(false);
  };

  // Delete holding
  const handleDeleteHolding = (symbol) => {
    if (!confirm(`Delete ${symbol} from portfolio?`)) return;

    const updatedHoldings = portfolio.filter((h) => h.symbol !== symbol);
    setPortfolio(updatedHoldings);

    // Update in portfolios array
    const updatedPortfolios = portfolios.map((p) =>
      p.id === currentPortfolioId ? { ...p, holdings: updatedHoldings } : p
    );
    setPortfolios(updatedPortfolios);
  };

  // Edit holding
  const handleEditHolding = (symbol) => {
    setEditingHolding(symbol);
  };

  const handleSaveEdit = (symbol) => {
    // Get the edited values from the DOM inputs
    const row = document.querySelector(`[data-editing="${symbol}"]`);
    if (!row) return;

    let newQuantity = parseFloat(row.querySelector(".edit-quantity")?.value);
    let newPrice = parseFloat(row.querySelector(".edit-price")?.value);
    const newAssetClass = row.querySelector(".edit-class")?.value;

    // Validate: prevent negative values
    newQuantity = Math.max(0, newQuantity || 0);
    newPrice = Math.max(0, newPrice || 0);

    if (!newQuantity || !newPrice) {
      alert("Quantity and Price must be greater than 0");
      return;
    }

    // Update the holding
    const updatedHoldings = portfolio.map((h) =>
      h.symbol === symbol
        ? {
            ...h,
            quantity: newQuantity,
            price: newPrice,
            marketValue: newQuantity * newPrice,
            assetClass: newAssetClass,
          }
        : h
    );

    setPortfolio(updatedHoldings);

    // Update in portfolios array
    const updatedPortfolios = portfolios.map((p) =>
      p.id === currentPortfolioId ? { ...p, holdings: updatedHoldings } : p
    );
    setPortfolios(updatedPortfolios);

    // Exit edit mode
    setEditingHolding(null);
  };

  const handleCancelEdit = () => {
    setEditingHolding(null);
  };

  // Manual upload functions - PURE DOM approach
  const manualRowsContainerRef = useRef(null);
  const manualRowIdCounter = useRef(0);

  const addManualRow = () => {
    if (!manualRowsContainerRef.current) return;

    const rowCount = manualRowsContainerRef.current.children.length;
    if (rowCount >= 10) return;

    const rowId = `manual-row-${manualRowIdCounter.current++}`;

    const rowHTML = `
      <div class="manual-table-row" id="${rowId}">
        <div><input type="text" placeholder="AAPL" class="manual-input manual-symbol" /></div>
        <div><input type="number" placeholder="10" class="manual-input manual-quantity" step="0.01" min="0" /></div>
        <div><input type="number" placeholder="150.00" class="manual-input manual-price" step="0.01" min="0" /></div>
        <div class="manual-value">$0</div>
        <div>
          <select class="manual-select manual-class">
            <option value="US Stocks">US Stocks</option>
            <option value="International Stocks">International</option>
            <option value="Bonds">Bonds</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Crypto">Crypto</option>
            <option value="Precious Metals">Precious Metals</option>
            <option value="Cash">Cash</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          ${
            rowCount > 0
              ? '<button class="remove-row-btn" title="Remove row">🗑️</button>'
              : ""
          }
        </div>
      </div>
    `;

    manualRowsContainerRef.current.insertAdjacentHTML("beforeend", rowHTML);

    // Add event listeners
    const newRow = document.getElementById(rowId);
    const qtyInput = newRow.querySelector(".manual-quantity");
    const priceInput = newRow.querySelector(".manual-price");
    const valueCell = newRow.querySelector(".manual-value");
    const symbolInput = newRow.querySelector(".manual-symbol");
    const removeBtn = newRow.querySelector(".remove-row-btn");

    const updateValue = () => {
      let qty = parseFloat(qtyInput.value) || 0;
      let price = parseFloat(priceInput.value) || 0;
      
      // Validate: prevent negative values
      qty = Math.max(0, qty);
      price = Math.max(0, price);
      
      // Update inputs if negative was entered
      if (parseFloat(qtyInput.value) < 0) qtyInput.value = 0;
      if (parseFloat(priceInput.value) < 0) priceInput.value = 0;
      
      valueCell.textContent = "$" + (qty * price).toLocaleString();
    };

    qtyInput.addEventListener("input", updateValue);
    priceInput.addEventListener("input", updateValue);
    symbolInput.addEventListener("input", (e) => {
      e.target.value = e.target.value.toUpperCase();
    });

    if (removeBtn) {
      removeBtn.addEventListener("click", () => {
        newRow.remove();
      });
    }
  };

  const clearManualRows = () => {
    if (manualRowsContainerRef.current) {
      manualRowsContainerRef.current.innerHTML = "";
      manualRowIdCounter.current = 0;
      addManualRow(); // Add first row
    }
  };

  const handleManualUploadFinish = async () => {
    if (!manualRowsContainerRef.current) return;

    const rows =
      manualRowsContainerRef.current.querySelectorAll(".manual-table-row");
    const validHoldings = [];

    rows.forEach((row) => {
      const symbol = row
        .querySelector(".manual-symbol")
        ?.value.trim()
        .toUpperCase();
      const quantity = row.querySelector(".manual-quantity")?.value;
      const price = row.querySelector(".manual-price")?.value;
      const assetClass = row.querySelector(".manual-class")?.value;

      if (symbol && quantity && price) {
        // Validate: prevent negative values
        const validQuantity = Math.max(0, parseFloat(quantity) || 0);
        const validPrice = Math.max(0, parseFloat(price) || 0);
        
        if (validQuantity > 0 && validPrice > 0) {
          validHoldings.push({
            symbol,
            quantity: validQuantity,
            price: validPrice,
            assetClass,
          });
        }
      }
    });

    if (validHoldings.length === 0) {
      alert("Please enter at least one holding with Symbol, Shares, and Price");
      return;
    }

    // Require authentication
    if (!user) {
      setShowLogin(true);
      return;
    }

    // Check if user can add portfolio (for new uploads)
    if (uploadMode === "new" && user) {
      const { canAdd } = await canAddPortfolio(user.uid);
      if (!canAdd) {
        setShowUpgrade(true);
        return;
      }
    }

    // Convert to portfolio format
    const holdings = validHoldings.map((h) => ({
      symbol: h.symbol,
      quantity: h.quantity,
      price: h.price,
      marketValue: h.quantity * h.price,
      costBasis: 0,
      assetClass:
        assetClassMap[h.symbol] ||
        customAssetMappings[h.symbol] ||
        h.assetClass,
    }));

    // Check for unknown tickers
    const unknown = holdings
      .filter((h) => !assetClassMap[h.symbol] && !customAssetMappings[h.symbol])
      .map((h) => h.symbol);

    if (unknown.length > 0) {
      setUnknownTickers(unknown);
    }

    if (uploadMode === "update" && currentPortfolioId) {
      // Update existing portfolio
      let finalHoldings;

      if (appendMode) {
        // Append mode: add new holdings to existing ones
        const existingPortfolio = portfolios.find(
          (p) => p.id === currentPortfolioId
        );
        const existingHoldings = existingPortfolio
          ? existingPortfolio.holdings
          : [];

        // Merge: update existing symbols, add new ones
        const holdingsMap = new Map();
        existingHoldings.forEach((h) => holdingsMap.set(h.symbol, h));
        holdings.forEach((h) => holdingsMap.set(h.symbol, h)); // New values overwrite if symbol exists

        finalHoldings = Array.from(holdingsMap.values());
      } else {
        // Replace mode: completely replace holdings
        finalHoldings = holdings;
      }

      const updatedPortfolios = portfolios.map((p) =>
        p.id === currentPortfolioId ? { ...p, holdings: finalHoldings } : p
      );
      setPortfolios(updatedPortfolios);
      setPortfolio(finalHoldings);
    } else {
      // Create new portfolio
      const newPortfolioId = Date.now().toString();
      const defaultName = `Manual Portfolio ${portfolios.length + 1}`;

      setPortfolioName(defaultName);
      setPortfolio(holdings);

      const newPortfolio = {
        id: newPortfolioId,
        name: defaultName,
        holdings,
      };
      setPortfolios([...portfolios, newPortfolio]);
      setCurrentPortfolioId(newPortfolioId);
      setShowAggregated(false);
    }

    // Reset manual entry
    setManualUploadMode(false);
    clearManualRows();

    // Show classification modal if needed
    if (unknown.length > 0) {
      setShowClassifyModal(true);
    } else {
      setView("portfolio");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(".csv")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const parsed = parseCSV(event.target.result);
        if (parsed) {
          setPortfolio(parsed);
          setView("portfolio");
        }
      };
      reader.readAsText(file);
    }
  };

  // Calculate current allocation
  const calculateCurrentAllocation = () => {
    const totalValue = portfolio.reduce((sum, h) => sum + h.marketValue, 0);
    const allocation = {};

    portfolio.forEach((holding) => {
      const assetClass = holding.assetClass;
      if (!allocation[assetClass]) {
        allocation[assetClass] = 0;
      }
      allocation[assetClass] += holding.marketValue;
    });

    return Object.entries(allocation).map(([name, value]) => ({
      name,
      value,
      percentage: (value / totalValue) * 100,
    }));
  };

  const calculateRebalancingNeeds = () => {
    const totalValue = portfolio.reduce((sum, h) => sum + h.marketValue, 0);
    const currentAllocation = calculateCurrentAllocation();

    const rebalanceActions = [];
    Object.entries(targetAllocation).forEach(([assetClass, targetPct]) => {
      const current = currentAllocation.find((a) => a.name === assetClass);
      const currentPct = current ? current.percentage : 0;
      const currentValue = current ? current.value : 0;
      const targetValue = (targetPct / 100) * totalValue;
      const difference = targetValue - currentValue;
      const drift = currentPct - targetPct;

      if (Math.abs(drift) >= rebalanceThreshold) {
        rebalanceActions.push({
          assetClass,
          currentPct: currentPct.toFixed(1),
          targetPct,
          drift: drift.toFixed(1),
          action: difference > 0 ? "BUY" : "SELL",
          amount: Math.abs(difference),
        });
      }
    });

    return rebalanceActions.sort(
      (a, b) => Math.abs(b.drift) - Math.abs(a.drift)
    );
  };

  const calculateNewMoneyDeployment = (newMoneyAmount) => {
    const totalValue = portfolio.reduce((sum, h) => sum + h.marketValue, 0);
    const newTotalValue = totalValue + newMoneyAmount;
    const currentAllocation = calculateCurrentAllocation();

    // Calculate how much each asset class needs in the new portfolio
    const deploymentPlan = [];
    let remainingMoney = newMoneyAmount;

    // First, calculate the gaps for each asset class
    const gaps = Object.entries(targetAllocation).map(
      ([assetClass, targetPct]) => {
        const current = currentAllocation.find((a) => a.name === assetClass);
        const currentValue = current ? current.value : 0;
        const targetValueInNewPortfolio = (targetPct / 100) * newTotalValue;
        const gap = targetValueInNewPortfolio - currentValue;

        return {
          assetClass,
          targetPct,
          currentValue,
          targetValueInNewPortfolio,
          gap,
          currentPct: current ? current.percentage : 0,
        };
      }
    );

    // Sort by gap (largest deficits first)
    const sortedGaps = gaps
      .filter((g) => g.gap > 0)
      .sort((a, b) => b.gap - a.gap);

    // Allocate new money proportionally to gaps
    sortedGaps.forEach((item) => {
      if (remainingMoney <= 0) return;

      const allocation = Math.min(item.gap, remainingMoney);
      if (allocation > 0) {
        deploymentPlan.push({
          assetClass: item.assetClass,
          amount: allocation,
          percentage: (allocation / newMoneyAmount) * 100,
          currentPct: item.currentPct.toFixed(1),
          targetPct: item.targetPct,
          newPct: (
            ((item.currentValue + allocation) / newTotalValue) *
            100
          ).toFixed(1),
        });
        remainingMoney -= allocation;
      }
    });

    return {
      deploymentPlan: deploymentPlan.sort((a, b) => b.amount - a.amount),
      totalDeployed: newMoneyAmount - remainingMoney,
      unallocated: remainingMoney,
    };
  };

  const COLORS = {
    "US Stocks": "#2563eb",
    "International Stocks": "#7c3aed",
    Bonds: "#059669",
    Crypto: "#f59e0b",
    "Precious Metals": "#eab308",
    "Real Estate": "#06b6d4",
    Cash: "#10b981",
    Other: "#64748b",
  };

  // Initialize manual entry when mode opens
  useEffect(() => {
    if (manualUploadMode && manualRowsContainerRef.current) {
      if (manualRowsContainerRef.current.children.length === 0) {
        addManualRow();
      }
    }
  }, [manualUploadMode]);

  // Views
  const UploadView = () => (
    <div className="upload-container">
      <div className="upload-header">
        {/* Left side - Back button OR empty */}
        {portfolios.length > 0 && (
          <button onClick={() => setView("portfolio")} className="back-button">
            ← Back to Portfolio
          </button>
        )}
        
        {/* Right side - Auth buttons OR User profile */}
        <div className="header-right">
          {!user ? (
            <div className="auth-buttons">
              <button
                onClick={() => setShowLogin(true)}
                className="signin-button-small"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowSignup(true)}
                className="signup-button-large"
              >
                Create Free Account
              </button>
            </div>
          ) : (
            <UserProfile
              user={user}
              userData={userData}
              onUpgrade={() => setShowUpgrade(true)}
              onLogout={() => {
                // Clear all portfolio data
                setPortfolios([]);
                setPortfolio([]);
                setCurrentPortfolioId(null);
                setPortfolioName("");
                setShowAggregated(false);
                // Stay on upload view
                setView("upload");
              }}
              portfolios={portfolios}
              customAssetMappings={customAssetMappings}
              rebalanceThreshold={rebalanceThreshold}
              customAllocation={customAllocation}
              targetAllocation={targetAllocation}
            />
          )}
        </div>
        
        {/* Center content */}
        <h1>Portfolio Rebalancing Assistant</h1>
        <p className="subtitle">Upload your portfolio CSV to get started</p>
      </div>

      {portfolios.length > 0 && (
        <div className="upload-mode-selector">
          <h3>What would you like to do?</h3>
          <div className="mode-buttons">
            <button
              className={`mode-btn ${uploadMode === "new" ? "active" : ""}`}
              onClick={() => {
                setUploadMode("new");
                setAppendMode(false);
              }}
            >
              <div className="mode-icon">➕</div>
              <div className="mode-label">Add New Portfolio</div>
              <div className="mode-desc">
                Track another account (401k, IRA, etc.)
              </div>
            </button>
            <button
              className={`mode-btn ${uploadMode === "update" ? "active" : ""}`}
              onClick={() => setUploadMode("update")}
            >
              <div className="mode-icon">🔄</div>
              <div className="mode-label">Update Existing Portfolio</div>
              <div className="mode-desc">
                Refresh holdings for: {portfolioName || "current portfolio"}
              </div>
            </button>
          </div>

          {uploadMode === "update" && (
            <div className="append-mode-toggle">
              <label className="append-checkbox">
                <input
                  type="checkbox"
                  checked={appendMode}
                  onChange={(e) => setAppendMode(e.target.checked)}
                />
                <span className="append-label">
                  <strong>Add to existing holdings</strong> instead of replacing
                  them
                </span>
              </label>
              <p className="append-hint">
                {appendMode
                  ? "✅ New holdings will be added. Existing symbols will be updated with new prices/quantities."
                  : "⚠️ All current holdings will be replaced with the new entries."}
              </p>
            </div>
          )}
        </div>
      )}

      {!manualUploadMode ? (
        <>
          <div
            className="upload-zone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <svg
              className="upload-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <h3>Drop your CSV file here</h3>
            <p>or click to browse</p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              id="file-upload"
            />
            <label htmlFor="file-upload" className="upload-button">
              {uploadMode === "update" ? "Update Portfolio" : "Choose File"}
            </label>
            {uploadMode === "update" && (
              <p className="upload-hint">
                This will replace holdings for: <strong>{portfolioName}</strong>
              </p>
            )}

            <div className="upload-divider">
              <span>OR</span>
            </div>

            <button
              className="manual-upload-btn"
              onClick={() => {
                if (!user) {
                  setShowLogin(true);
                } else {
                  setManualUploadMode(true);
                }
              }}
            >
              ✍️ Enter Holdings Manually
            </button>
          </div>

          {csvError && <div className="error-message">{csvError}</div>}

          <div className="csv-help">
            <h3>Supported CSV Formats</h3>
            <p>Your CSV should include columns for:</p>
            <ul>
              <li>Symbol/Ticker (required)</li>
              <li>Quantity/Shares</li>
              <li>Price or Market Value</li>
              <li>Cost Basis (optional)</li>
            </ul>
            <p className="help-note">
              Compatible with exports from Vanguard, Fidelity, Schwab, and most
              major brokerages
            </p>
          </div>
        </>
      ) : (
        <div className="manual-entry-container">
          <h3>📝 Enter Your Holdings</h3>
          <p className="manual-entry-subtitle">
            Add up to 10 holdings manually
          </p>

          <div className="manual-entry-table">
            <div className="manual-table-header">
              <div>Symbol</div>
              <div>Shares</div>
              <div>Price</div>
              <div>Value</div>
              <div>Asset Class</div>
              <div>Actions</div>
            </div>

            {/* Pure DOM rows - no React rendering */}
            <div ref={manualRowsContainerRef}></div>
          </div>

          <div className="manual-entry-actions">
            <button className="add-row-btn" onClick={addManualRow}>
              + Add Another Row
            </button>
            <div className="manual-entry-buttons">
              <button
                className="btn-secondary"
                onClick={() => {
                  setManualUploadMode(false);
                  clearManualRows();
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleManualUploadFinish}
              >
                Finish & Create Portfolio
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const PortfolioView = () => {
    const currentAllocation = calculateCurrentAllocation();
    const displayPortfolio = getCurrentPortfolio();
    const totalValue = displayPortfolio.reduce(
      (sum, h) => sum + h.marketValue,
      0
    );

    return (
      <div className="portfolio-container">
        <div className="nav-header">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {editingNameId === currentPortfolioId ? (
              <input
                type="text"
                value={portfolioName}
                onChange={(e) => {
                  setPortfolioName(e.target.value);
                  updatePortfolioName(currentPortfolioId, e.target.value);
                }}
                onBlur={() => setEditingNameId(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setEditingNameId(null);
                  if (e.key === "Escape") {
                    setEditingNameId(null);
                    // Reset to original name from portfolios
                    const original = portfolios.find(
                      (p) => p.id === currentPortfolioId
                    );
                    if (original) setPortfolioName(original.name);
                  }
                }}
                autoFocus
                style={{
                  background: "rgba(15, 23, 42, 0.8)",
                  border: "2px solid #3b82f6",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  color: "#f1f5f9",
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  outline: "none",
                  maxWidth: "400px",
                }}
              />
            ) : (
              <>
                <h2>
                  {showAggregated
                    ? "All Portfolios (Aggregated)"
                    : portfolioName || "Your Portfolio"}
                </h2>
                {!showAggregated && currentPortfolioId && (
                  <button
                    onClick={() => setEditingNameId(currentPortfolioId)}
                    style={{
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "2px solid #3b82f6",
                      borderRadius: "6px",
                      padding: "6px 10px",
                      cursor: "pointer",
                      fontSize: "1rem",
                      transition: "all 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.2)";
                      e.target.style.transform = "scale(1.1)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.transform = "scale(1)";
                    }}
                    title="Rename portfolio"
                  >
                    ✏️
                  </button>
                )}
              </>
            )}
          </div>
          <div className="nav-buttons">
            {user && (
              <UserProfile
                user={user}
                userData={userData}
                onUpgrade={() => setShowUpgrade(true)}
                onLogout={() => {
                  // Clear all portfolio data
                  setPortfolios([]);
                  setPortfolio([]);
                  setCurrentPortfolioId(null);
                  setPortfolioName("");
                  setShowAggregated(false);
                  // Go to upload view
                  setView("upload");
                  // Don't auto-show login modal - let user choose
                }}
                portfolios={portfolios}
                customAssetMappings={customAssetMappings}
                rebalanceThreshold={rebalanceThreshold}
                customAllocation={customAllocation}
                targetAllocation={targetAllocation}
              />
            )}
            <button
              onClick={() => {
                // Reset all upload-related state
                setUploadMode("new");
                setAppendMode(false);
                setManualUploadMode(false);
                clearManualRows();
                setCsvError("");
                setUnknownTickers([]);
                setView("upload");
              }}
              className="btn-secondary"
            >
              New Upload
            </button>
            <button
              onClick={() => setView("rebalance")}
              className="btn-primary"
            >
              Rebalance
            </button>
            <button
              onClick={() => setView("montecarlo")}
              className="btn-primary"
            >
              Monte Carlo
            </button>
          </div>
        </div>

        {portfolios.length > 1 && (
          <div className="portfolio-switcher">
            <h3>Your Portfolios</h3>
            <div className="portfolio-tabs">
              {portfolios.map((p) => (
                <div
                  key={p.id}
                  className={`portfolio-tab-wrapper ${
                    currentPortfolioId === p.id && !showAggregated
                      ? "active"
                      : ""
                  }`}
                >
                  {editingNameId === p.id ? (
                    <div className="portfolio-name-edit">
                      <input
                        type="text"
                        value={p.name}
                        placeholder="e.g., Vanguard 401k, IRA, Roth..."
                        onChange={(e) =>
                          updatePortfolioName(p.id, e.target.value)
                        }
                        onBlur={() => setEditingNameId(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setEditingNameId(null);
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <button
                      className="portfolio-tab"
                      onClick={() => switchPortfolio(p.id)}
                    >
                      <div className="portfolio-tab-content">
                        <span className="portfolio-tab-name">{p.name}</span>
                        <span className="tab-value">
                          $
                          {p.holdings
                            .reduce((sum, h) => sum + h.marketValue, 0)
                            .toLocaleString()}
                        </span>
                      </div>
                    </button>
                  )}
                  <div className="portfolio-tab-actions">
                    <button
                      className="tab-action-btn edit"
                      onClick={() => setEditingNameId(p.id)}
                      title="Rename portfolio"
                    >
                      ✏️
                    </button>
                    <button
                      className="tab-action-btn delete"
                      onClick={() => {
                        if (confirm(`Delete "${p.name}"?`)) {
                          deletePortfolio(p.id);
                        }
                      }}
                      title="Delete portfolio"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
              <button
                className={`portfolio-tab ${showAggregated ? "active" : ""}`}
                onClick={viewAggregated}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    📊 All Portfolios
                    {userData?.tier !== "premium" && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontWeight: "700",
                        }}
                      >
                        PREMIUM
                      </span>
                    )}
                  </div>
                  <span className="tab-value">
                    $
                    {portfolios
                      .reduce(
                        (sum, p) =>
                          sum +
                          p.holdings.reduce((s, h) => s + h.marketValue, 0),
                        0
                      )
                      .toLocaleString()}
                  </span>
                </div>
              </button>
            </div>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Value</div>
            <div className="stat-value">
              $
              {totalValue.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Holdings</div>
            <div className="stat-value">{displayPortfolio.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Asset Classes</div>
            <div className="stat-value">{currentAllocation.length}</div>
          </div>
          {portfolios.length > 1 && (
            <div className="stat-card">
              <div className="stat-label">Portfolios</div>
              <div className="stat-value">{portfolios.length}</div>
            </div>
          )}
        </div>

        <div className="content-grid">
          <div className="chart-section">
            <h3>Current Allocation</h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={currentAllocation}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {currentAllocation.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `$${value.toLocaleString()}`,
                    `${props.payload.name} (${props.payload.percentage.toFixed(1)}%)`
                  ]}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={80}
                  formatter={(value, entry) => {
                    const item = currentAllocation.find(a => a.name === value);
                    return `${value}: ${item ? item.percentage.toFixed(1) : 0}%`;
                  }}
                  wrapperStyle={{
                    paddingTop: '20px',
                    fontSize: '0.875rem'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="holdings-section">
            <h3>Holdings Detail</h3>
            <div className="holdings-table">
              <div className="holdings-scroll-container">
                <div className="table-header">
                  <div>Symbol</div>
                  <div>Shares</div>
                  <div>Price</div>
                  <div>Value</div>
                  <div>Class</div>
                  <div>Actions</div>
                </div>

                {displayPortfolio.map((holding, idx) =>
                  editingHolding === holding.symbol ? (
                    // Edit mode
                    <div
                      key={idx}
                      className="table-row edit-mode"
                      data-editing={holding.symbol}
                    >
                      <div className="symbol">{holding.symbol}</div>
                      <div>
                        <input
                          type="number"
                          defaultValue={holding.quantity}
                          className="edit-input edit-quantity"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          defaultValue={holding.price}
                          className="edit-input edit-price"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="value">
                        $
                        {(
                          (parseFloat(
                            document.querySelector(
                              `[data-editing="${holding.symbol}"] .edit-quantity`
                            )?.value
                          ) || holding.quantity) *
                          (parseFloat(
                            document.querySelector(
                              `[data-editing="${holding.symbol}"] .edit-price`
                            )?.value
                          ) || holding.price)
                        ).toLocaleString()}
                      </div>
                      <div>
                        <select
                          defaultValue={holding.assetClass}
                          className="edit-select edit-class"
                        >
                          <option value="US Stocks">US Stocks</option>
                          <option value="International Stocks">
                            International
                          </option>
                          <option value="Bonds">Bonds</option>
                          <option value="Real Estate">Real Estate</option>
                          <option value="Crypto">Crypto</option>
                          <option value="Precious Metals">
                            Precious Metals
                          </option>
                          <option value="Cash">Cash</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button
                          className="save-edit-btn"
                          onClick={() => handleSaveEdit(holding.symbol)}
                          title="Save changes"
                        >
                          ✓
                        </button>
                        <button
                          className="cancel-edit-btn"
                          onClick={handleCancelEdit}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div key={idx} className="table-row">
                      <div className="symbol">{holding.symbol}</div>
                      <div>{holding.quantity.toFixed(2)}</div>
                      <div>${holding.price.toFixed(2)}</div>
                      <div className="value">
                        ${holding.marketValue.toLocaleString()}
                      </div>
                      <div>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: COLORS[holding.assetClass] + "20",
                            color: COLORS[holding.assetClass],
                          }}
                        >
                          {holding.assetClass}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button
                          className="edit-holding-btn"
                          onClick={() => handleEditHolding(holding.symbol)}
                          title="Edit holding"
                        >
                          ✏️
                        </button>
                        <button
                          className="delete-holding-btn"
                          onClick={() => handleDeleteHolding(holding.symbol)}
                          title="Delete holding"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Add Holding Form - Outside scroll container */}
              {showAddHolding ? (
                <div className="table-row add-holding-form" ref={addFormRef}>
                  <div>
                    <input
                      ref={symbolInputRef}
                      type="text"
                      placeholder="AAPL"
                      className="holding-input"
                      onInput={(e) =>
                        (e.target.value = e.target.value.toUpperCase())
                      }
                    />
                  </div>
                  <div>
                    <input
                      ref={quantityInputRef}
                      type="number"
                      placeholder="10"
                      className="holding-input"
                      step="0.01"
                      min="0"
                      onInput={() => {
                        let qty = parseFloat(quantityInputRef.current?.value) || 0;
                        let price = parseFloat(priceInputRef.current?.value) || 0;
                        
                        // Validate: prevent negative values
                        qty = Math.max(0, qty);
                        price = Math.max(0, price);
                        
                        // Reset input if negative was entered
                        if (quantityInputRef.current && parseFloat(quantityInputRef.current.value) < 0) {
                          quantityInputRef.current.value = '0';
                        }
                        
                        const valueSpan = document.getElementById("calculated-value");
                        if (valueSpan) {
                          valueSpan.textContent = "$" + (qty * price).toLocaleString();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <input
                      ref={priceInputRef}
                      type="number"
                      placeholder="150.00"
                      className="holding-input"
                      step="0.01"
                      min="0"
                      onInput={() => {
                        let qty = parseFloat(quantityInputRef.current?.value) || 0;
                        let price = parseFloat(priceInputRef.current?.value) || 0;
                        
                        // Validate: prevent negative values
                        qty = Math.max(0, qty);
                        price = Math.max(0, price);
                        
                        // Reset input if negative was entered
                        if (priceInputRef.current && parseFloat(priceInputRef.current.value) < 0) {
                          priceInputRef.current.value = '0';
                        }
                        
                        const valueSpan = document.getElementById("calculated-value");
                        if (valueSpan) {
                          valueSpan.textContent = "$" + (qty * price).toLocaleString();
                        }
                      }}
                    />
                  </div>
                  <div className="value">
                    <span id="calculated-value">$0</span>
                  </div>
                  <div>
                    <select
                      ref={classSelectRef}
                      defaultValue="US Stocks"
                      className="holding-select"
                    >
                      <option value="US Stocks">US Stocks</option>
                      <option value="International Stocks">
                        International
                      </option>
                      <option value="Bonds">Bonds</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Crypto">Crypto</option>
                      <option value="Precious Metals">Precious Metals</option>
                      <option value="Cash">Cash</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      className="save-holding-btn"
                      onClick={handleAddHolding}
                      title="Save holding"
                    >
                      ✓
                    </button>
                    <button
                      className="cancel-holding-btn"
                      onClick={() => {
                        setShowAddHolding(false);
                        // Reset refs
                        if (symbolInputRef.current)
                          symbolInputRef.current.value = "";
                        if (quantityInputRef.current)
                          quantityInputRef.current.value = "";
                        if (priceInputRef.current)
                          priceInputRef.current.value = "";
                        if (classSelectRef.current)
                          classSelectRef.current.value = "US Stocks";
                      }}
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="add-holding-btn"
                  onClick={() => {
                    setShowAddHolding(true);
                    // Scroll to form after it renders
                    setTimeout(() => {
                      if (addFormRef.current) {
                        addFormRef.current.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                        // Focus first input
                        const firstInput =
                          addFormRef.current.querySelector("input");
                        if (firstInput) firstInput.focus();
                      }
                    }, 100);
                  }}
                >
                  + Add Holding Manually
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Diversification Score Analysis */}
        {(() => {
          const divScore = calculateDiversificationScore();
          if (!divScore) return null;

          return (
            <div className="diversification-section">
              <h3>📊 Portfolio Diversification Analysis</h3>

              <div className="div-score-card">
                <div className="div-score-main">
                  <div className="div-score-label">Diversification Score</div>
                  <div
                    className={`div-score-value ${divScore.rating.toLowerCase()}`}
                  >
                    {divScore.score.toFixed(2)}
                  </div>
                  <div className="div-score-rating">{divScore.rating}</div>
                </div>

                <div className="div-score-explanation">
                  <div className="div-explanation-text">
                    This score measures how independently your assets move. Lower is better (0 = completely independent, 1 = move together perfectly). Your score of <strong>{divScore.score.toFixed(2)}</strong> means your assets are {divScore.rating.toLowerCase()}ly diversified.
                  </div>
                </div>
              </div>

              <div className="div-metrics-grid">
                <div className="div-metric-card">
                  <div className="div-metric-icon">📈</div>
                  <div className="div-metric-content">
                    <div className="div-metric-label">Growth-Focused Assets</div>
                    <div className="div-metric-value">
                      {divScore.highlyCorrelated.toFixed(0)}%
                    </div>
                    <div className="div-metric-note">
                      Stocks, Real Estate, Crypto (tend to rise together)
                    </div>
                  </div>
                </div>

                <div className="div-metric-card">
                  <div className="div-metric-icon">🛡️</div>
                  <div className="div-metric-content">
                    <div className="div-metric-label">Protective Assets</div>
                    <div className="div-metric-value">
                      {divScore.diversifiers.toFixed(0)}%
                    </div>
                    <div className="div-metric-note">
                      Bonds, Precious Metals, Cash (protect in downturns)
                    </div>
                  </div>
                </div>
              </div>

              <div className="div-insights">
                <h4>What This Means for Your Portfolio</h4>

                {divScore.score < 0.3 && (
                  <div className="div-insight excellent">
                    <div className="div-insight-icon">✅</div>
                    <div className="div-insight-text">
                      <strong>Excellent Diversification!</strong>
                      <p>
                        Your assets have low correlation, meaning they won't all
                        crash together. This provides strong downside
                        protection.
                      </p>
                    </div>
                  </div>
                )}

                {divScore.score >= 0.3 && divScore.score < 0.5 && (
                  <div className="div-insight good">
                    <div className="div-insight-icon">👍</div>
                    <div className="div-insight-text">
                      <strong>Good Diversification</strong>
                      <p>
                        Your portfolio has reasonable diversification. Most
                        assets won't move in perfect lockstep, providing some
                        crash protection.
                      </p>
                    </div>
                  </div>
                )}

                {divScore.score >= 0.5 && divScore.score < 0.7 && (
                  <div className="div-insight moderate">
                    <div className="div-insight-icon">⚠️</div>
                    <div className="div-insight-text">
                      <strong>Moderate Diversification</strong>
                      <p>
                        Many of your assets tend to move together. Consider
                        adding more bonds or precious metals for better crash
                        protection.
                      </p>
                    </div>
                  </div>
                )}

                {divScore.score >= 0.7 && (
                  <div className="div-insight poor">
                    <div className="div-insight-icon">❌</div>
                    <div className="div-insight-text">
                      <strong>Poor Diversification</strong>
                      <p>
                        Your assets are highly correlated - they'll likely crash
                        together. You need more bonds, precious metals, or cash
                        to reduce risk.
                      </p>
                    </div>
                  </div>
                )}

                {/* Only show concentration warning if score is not already poor */}
                {divScore.highlyCorrelated > 85 && divScore.score < 0.7 && (
                  <div className="div-insight warning">
                    <div className="div-insight-icon">💡</div>
                    <div className="div-insight-text">
                      <strong>Portfolio Concentration Note</strong>
                      <p>
                        {divScore.highlyCorrelated.toFixed(0)}% of your
                        portfolio is in growth-focused assets (stocks, real
                        estate, crypto). While your current mix has decent diversification, consider whether this concentration matches your risk tolerance during market downturns.
                      </p>
                    </div>
                  </div>
                )}

                {/* Only show diversifier suggestion if we haven't already flagged poor diversification */}
                {divScore.diversifiers < 15 && divScore.score < 0.7 && (
                  <div className="div-insight warning">
                    <div className="div-insight-icon">🛡️</div>
                    <div className="div-insight-text">
                      <strong>Consider More Protection</strong>
                      <p>
                        Only {divScore.diversifiers.toFixed(0)}% of your
                        portfolio is in protective assets. Consider increasing bonds or
                        precious metals to 20-30% for cushion during market crashes.
                      </p>
                    </div>
                  </div>
                )}

                {divScore.diversifiers >= 15 && divScore.diversifiers < 30 && (
                  <div className="div-insight success">
                    <div className="div-insight-icon">✨</div>
                    <div className="div-insight-text">
                      <strong>Solid Protective Balance</strong>
                      <p>
                        You have {divScore.diversifiers.toFixed(0)}% in protective assets
                        (bonds, precious metals, cash). This provides
                        meaningful cushion during stock market downturns.
                      </p>
                    </div>
                  </div>
                )}

                {divScore.diversifiers >= 30 && (
                  <div className="div-insight success">
                    <div className="div-insight-icon">🛡️</div>
                    <div className="div-insight-text">
                      <strong>Strong Defensive Position</strong>
                      <p>
                        You have {divScore.diversifiers.toFixed(0)}% in protective assets.
                        This high allocation to bonds, precious metals, and cash provides
                        excellent downside protection, though it may limit upside growth.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="div-reference">
                <h4>Reference: Asset Correlations</h4>
                <div className="div-reference-grid">
                  <div className="div-ref-item">
                    <span className="div-ref-pair">
                      US Stocks ↔ International Stocks
                    </span>
                    <span className="div-ref-value high">0.85</span>
                  </div>
                  <div className="div-ref-item">
                    <span className="div-ref-pair">
                      US Stocks ↔ Real Estate
                    </span>
                    <span className="div-ref-value high">0.70</span>
                  </div>
                  <div className="div-ref-item">
                    <span className="div-ref-pair">US Stocks ↔ Crypto</span>
                    <span className="div-ref-value medium">0.45</span>
                  </div>
                  <div className="div-ref-item">
                    <span className="div-ref-pair">US Stocks ↔ Bonds</span>
                    <span className="div-ref-value low">-0.10</span>
                  </div>
                  <div className="div-ref-item">
                    <span className="div-ref-pair">
                      US Stocks ↔ Precious Metals
                    </span>
                    <span className="div-ref-value low">0.15</span>
                  </div>
                  <div className="div-ref-item">
                    <span className="div-ref-pair">US Stocks ↔ Cash</span>
                    <span className="div-ref-value low">0.00</span>
                  </div>
                </div>
                <p className="div-ref-note">
                  Based on long-term historical correlations. These
                  relationships can change over time.
                </p>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const RebalanceView = () => {
    const currentAllocation = calculateCurrentAllocation();
    const totalValue = portfolio.reduce((sum, h) => sum + h.marketValue, 0);
    const rebalanceActions = calculateRebalancingNeeds();
    const needsRebalancing = rebalanceActions.length > 0;
    const newMoneyPlan = calculateNewMoneyDeployment(newMoneyAmount);

    // Local state for inputs to prevent one-character-at-a-time typing issue
    const [localAllocation, setLocalAllocation] = React.useState(targetAllocation);
    const [localThreshold, setLocalThreshold] = React.useState(rebalanceThreshold);
    const [localNewMoney, setLocalNewMoney] = React.useState(newMoneyAmount);

    // Sync local state when targetAllocation changes (e.g., preset change)
    React.useEffect(() => {
      setLocalAllocation(targetAllocation);
    }, [JSON.stringify(targetAllocation)]);

    React.useEffect(() => {
      setLocalThreshold(rebalanceThreshold);
    }, [rebalanceThreshold]);

    React.useEffect(() => {
      setLocalNewMoney(newMoneyAmount);
    }, [newMoneyAmount]);

    const targetData = Object.entries(targetAllocation).map(
      ([name, percentage]) => ({
        name,
        value: (percentage / 100) * totalValue,
        percentage,
      })
    );

    return (
      <div className="rebalance-container">
        <div className="nav-header">
          <h2>Rebalancing Analysis</h2>
          <div className="nav-buttons">
            {user && (
              <UserProfile
                user={user}
                userData={userData}
                onUpgrade={() => setShowUpgrade(true)}
                onLogout={() => {
                  setPortfolios([]);
                  setPortfolio([]);
                  setCurrentPortfolioId(null);
                  setPortfolioName("");
                  setShowAggregated(false);
                  setView("upload");
                }}
                portfolios={portfolios}
                customAssetMappings={customAssetMappings}
                rebalanceThreshold={rebalanceThreshold}
                customAllocation={customAllocation}
                targetAllocation={targetAllocation}
              />
            )}
            <button
              onClick={() => setView("portfolio")}
              className="btn-secondary"
            >
              Back to Portfolio
            </button>
          </div>
        </div>

        <div className="rebalance-settings">
          <h3>Target Allocation</h3>

          <div className="preset-selector">
            <button
              className={`preset-btn ${
                allocationPreset === "aggressive" ? "active" : ""
              }`}
              onClick={() => applyPreset("aggressive")}
            >
              <div className="preset-icon">🚀</div>
              <div className="preset-label">Aggressive</div>
              <div className="preset-desc">High growth focus</div>
            </button>
            <button
              className={`preset-btn ${
                allocationPreset === "moderate" ? "active" : ""
              }`}
              onClick={() => applyPreset("moderate")}
            >
              <div className="preset-icon">⚖️</div>
              <div className="preset-label">Moderate</div>
              <div className="preset-desc">Balanced approach</div>
            </button>
            <button
              className={`preset-btn ${
                allocationPreset === "conservative" ? "active" : ""
              }`}
              onClick={() => applyPreset("conservative")}
            >
              <div className="preset-icon">🛡️</div>
              <div className="preset-label">Conservative</div>
              <div className="preset-desc">Capital preservation</div>
            </button>
            <button
              className={`preset-btn ${
                allocationPreset === "custom" ? "active" : ""
              }`}
              onClick={() => applyPreset("custom")}
            >
              <div className="preset-icon">⚙️</div>
              <div className="preset-label">Custom</div>
              <div className="preset-desc">Your allocation</div>
            </button>
          </div>

          {/* Show "Use This Allocation" button for presets */}
          {allocationPreset !== "custom" && (
            <div className="use-preset-section">
              <button
                className="use-preset-btn"
                onClick={() => {
                  setCustomAllocation({ ...targetAllocation });
                  setAllocationPreset("custom");
                }}
              >
                📋 Use This Allocation
              </button>
              <p className="use-preset-hint">
                Click to copy these percentages to Custom where you can edit them
              </p>
            </div>
          )}

          {/* Allocation display - read-only for presets, editable for custom */}
          <div className="allocation-inputs">
            {Object.entries(targetAllocation).map(([assetClass, pct]) => (
              <div key={assetClass} className="allocation-input">
                <label>
                  {assetClass}
                  <span
                    className="tooltip-icon"
                    title={assetClassExplanations[assetClass]}
                  >
                    ⓘ
                  </span>
                </label>
                
                {allocationPreset === "custom" ? (
                  // Editable input for custom mode
                  <div className="input-group">
                    <input
                      type="number"
                      value={localAllocation[assetClass] || 0}
                      onChange={(e) => {
                        // Update local state immediately for smooth typing
                        setLocalAllocation({
                          ...localAllocation,
                          [assetClass]: e.target.value === '' ? 0 : parseFloat(e.target.value),
                        });
                      }}
                      onBlur={(e) => {
                        // Update global state on blur
                        const newValue = parseFloat(e.target.value) || 0;
                        const newAllocation = {
                          ...targetAllocation,
                          [assetClass]: newValue,
                        };
                        setTargetAllocation(newAllocation);
                        setCustomAllocation(newAllocation);
                      }}
                      min="0"
                      max="100"
                    />
                    <span>%</span>
                  </div>
                ) : (
                  // Read-only display for presets
                  <div className="preset-value">
                    <span className="value-number">{pct}%</span>
                    <span className="locked-icon">🔒</span>
                  </div>
                )}
                
                <div className="tooltip-text">
                  {assetClassExplanations[assetClass]}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`allocation-total ${
              getTotalAllocation() !== 100 ? "warning" : "success"
            }`}
          >
            <div className="total-label">Total Allocation:</div>
            <div className="total-value">{getTotalAllocation()}%</div>
            {getTotalAllocation() !== 100 && (
              <div className="total-message">
                {getTotalAllocation() < 100
                  ? `Add ${(100 - getTotalAllocation()).toFixed(
                      1
                    )}% more to reach 100%`
                  : `Reduce by ${(getTotalAllocation() - 100).toFixed(
                      1
                    )}% to reach 100%`}
              </div>
            )}
          </div>

          <div className="threshold-setting">
            <label>
              Rebalance Threshold (drift %)
            </label>
            <input
              type="number"
              value={localThreshold}
              onChange={(e) => {
                // Update local state immediately for smooth typing
                setLocalThreshold(e.target.value === '' ? '' : parseFloat(e.target.value));
              }}
              onBlur={(e) => {
                // Update global state on blur
                setRebalanceThreshold(parseFloat(e.target.value) || 5);
              }}
              min="1"
              max="20"
            />
            <div className="threshold-tooltip">
              How far an asset class can drift from target before rebalancing.
              Lower thresholds (2-3%) mean more frequent rebalancing. Higher
              thresholds (10-15%) allow more drift. 5% is a common sweet spot.
            </div>
          </div>
        </div>

        <div className="comparison-grid">
          <div className="chart-section">
            <h3>
              Current vs Target
              <span
                className="tooltip-icon"
                title="Shows how your current allocation compares to your target. The rebalancing actions below are based on the target allocation and threshold you set above."
              >
                ⓘ
              </span>
            </h3>
            <div className="allocation-bars">
              {Object.keys(targetAllocation).map((assetClass) => {
                const current = currentAllocation.find(
                  (a) => a.name === assetClass
                );
                const currentPct = current ? current.percentage : 0;
                const targetPct = targetAllocation[assetClass];
                const drift = currentPct - targetPct;

                return (
                  <div key={assetClass} className="allocation-bar">
                    <div className="bar-label">
                      <span>{assetClass}</span>
                      <span
                        className={`drift ${
                          Math.abs(drift) >= rebalanceThreshold ? "alert" : ""
                        }`}
                      >
                        {drift > 0 ? "+" : ""}
                        {drift.toFixed(1)}%
                      </span>
                    </div>
                    <div className="bar-container">
                      <div
                        className="bar current"
                        style={{
                          width: `${currentPct}%`,
                          backgroundColor: COLORS[assetClass],
                        }}
                      >
                        <span>{currentPct.toFixed(1)}%</span>
                      </div>
                      <div
                        className="bar-target"
                        style={{
                          left: `${targetPct}%`,
                          borderColor: COLORS[assetClass],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="actions-section">
            <h3>Rebalance Actions</h3>
            <p className="actions-disclaimer">
              Based on your target allocation and {rebalanceThreshold}% threshold above
            </p>
            {!needsRebalancing ? (
              <div className="success-message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p>Your portfolio is well-balanced!</p>
                <p className="sub">
                  All asset classes are within your {rebalanceThreshold}%
                  threshold.
                </p>
              </div>
            ) : (
              <div className="actions-list">
                {rebalanceActions.map((action, idx) => (
                  <div
                    key={idx}
                    className={`action-card ${action.action.toLowerCase()}`}
                  >
                    <div className="action-header">
                      <span className="action-badge">{action.action}</span>
                      <span className="drift-badge">
                        {action.drift > 0 ? "+" : ""}
                        {action.drift}%
                      </span>
                    </div>
                    <div className="action-body">
                      <h4>{action.assetClass}</h4>
                      <div className="action-details">
                        <div className="detail">
                          <span className="label">Current</span>
                          <span className="value">{action.currentPct}%</span>
                        </div>
                        <div className="detail">
                          <span className="label">Target</span>
                          <span className="value">{action.targetPct}%</span>
                        </div>
                      </div>
                      <div className="action-amount">
                        $
                        {action.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* New Money Deployment Section */}
        <div className="new-money-section">
          <div className="new-money-header">
            <h3>💰 Tax-Efficient Rebalancing with New Money</h3>
            <p className="new-money-subtitle">
              Rebalance without selling by directing new contributions
              strategically
            </p>
          </div>

          <div className="new-money-input-container">
            <label>
              How much are you adding to your portfolio?
            </label>
            <div className="new-money-input-group">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                value={localNewMoney}
                onChange={(e) => {
                  // Update local state immediately for smooth typing
                  setLocalNewMoney(e.target.value === '' ? '' : parseFloat(e.target.value));
                }}
                onBlur={(e) => {
                  // Update global state on blur
                  setNewMoneyAmount(parseFloat(e.target.value) || 0);
                }}
                min="0"
                step="100"
                placeholder="1000"
              />
            </div>
          </div>

          {newMoneyAmount > 0 && (
            <div className="deployment-plan">
              <h4>
                Deployment Strategy for ${newMoneyAmount.toLocaleString()}
              </h4>
              <div className="deployment-list">
                {newMoneyPlan.deploymentPlan.map((item, idx) => (
                  <div key={idx} className="deployment-item">
                    <div className="deployment-header">
                      <div className="deployment-class">
                        <span
                          className="asset-badge"
                          style={{
                            backgroundColor: COLORS[item.assetClass] + "20",
                            color: COLORS[item.assetClass],
                          }}
                        >
                          {item.assetClass}
                        </span>
                      </div>
                      <div className="deployment-amount">
                        $
                        {item.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    </div>
                    <div className="deployment-details">
                      <div className="deployment-bar-container">
                        <div
                          className="deployment-bar"
                          style={{
                            width: `${item.percentage}%`,
                            backgroundColor: COLORS[item.assetClass],
                          }}
                        ></div>
                      </div>
                      <div className="deployment-stats">
                        <span className="stat-item">
                          Current: {item.currentPct}%
                        </span>
                        <span className="stat-arrow">→</span>
                        <span className="stat-item new">
                          New: {item.newPct}%
                        </span>
                        <span className="stat-item target">
                          (Target: {item.targetPct}%)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {newMoneyPlan.unallocated > 1 && (
                <div className="unallocated-notice">
                  <strong>${newMoneyPlan.unallocated.toLocaleString()}</strong>{" "}
                  remaining - your portfolio would be perfectly balanced!
                  Consider adding this to your best-performing asset class or
                  keeping as cash.
                </div>
              )}

              <div className="deployment-benefits">
                <div className="benefit-card">
                  <div className="benefit-icon">✅</div>
                  <div className="benefit-text">
                    <strong>Tax-Free</strong>
                    <p>No capital gains taxes by avoiding sales</p>
                  </div>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">📈</div>
                  <div className="benefit-text">
                    <strong>Dollar-Cost Averaging</strong>
                    <p>Stay invested while rebalancing</p>
                  </div>
                </div>
                <div className="benefit-card">
                  <div className="benefit-icon">💰</div>
                  <div className="benefit-text">
                    <strong>No Transaction Costs</strong>
                    <p>Avoid bid-ask spreads and fees from selling</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Classify Tickers Modal Component
  const ClassifyTickersModal = () => {
    const [classifications, setClassifications] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!showClassifyModal || unknownTickers.length === 0) return null;

    const currentTicker = unknownTickers[currentIndex];
    const isLastTicker = currentIndex === unknownTickers.length - 1;
    const progress = ((currentIndex + 1) / unknownTickers.length) * 100;

    const handleClassify = (assetClass) => {
      const newClassifications = {
        ...classifications,
        [currentTicker]: assetClass,
      };
      setClassifications(newClassifications);

      if (isLastTicker) {
        // Done with all tickers
        handleClassifyComplete(newClassifications);
      } else {
        // Move to next ticker
        setCurrentIndex(currentIndex + 1);
      }
    };

    const handleSkip = () => {
      const newClassifications = {
        ...classifications,
        [currentTicker]: "Other",
      };
      setClassifications(newClassifications);

      if (isLastTicker) {
        handleClassifyComplete(newClassifications);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    };

    return (
      <div className="auth-modal-overlay">
        <div className="classify-modal">
          <h2>🏷️ Classify Your Holdings</h2>
          <p className="classify-subtitle">
            We found {unknownTickers.length} ticker
            {unknownTickers.length > 1 ? "s" : ""} we don't recognize. Help us
            categorize them!
          </p>

          <div className="classify-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="progress-text">
              {currentIndex + 1} of {unknownTickers.length}
            </span>
          </div>

          <div className="classify-ticker">
            <div className="ticker-badge">{currentTicker}</div>
            <p className="classify-question">What type of asset is this?</p>
          </div>

          <div className="classify-options">
            <button
              className="classify-btn us-stocks"
              onClick={() => handleClassify("US Stocks")}
            >
              <span className="classify-icon">🇺🇸</span>
              <span className="classify-label">US Stocks</span>
              <span className="classify-desc">US companies & ETFs</span>
            </button>

            <button
              className="classify-btn intl-stocks"
              onClick={() => handleClassify("International Stocks")}
            >
              <span className="classify-icon">🌍</span>
              <span className="classify-label">International</span>
              <span className="classify-desc">Foreign companies</span>
            </button>

            <button
              className="classify-btn bonds"
              onClick={() => handleClassify("Bonds")}
            >
              <span className="classify-icon">📜</span>
              <span className="classify-label">Bonds</span>
              <span className="classify-desc">Fixed income</span>
            </button>

            <button
              className="classify-btn real-estate"
              onClick={() => handleClassify("Real Estate")}
            >
              <span className="classify-icon">🏘️</span>
              <span className="classify-label">Real Estate</span>
              <span className="classify-desc">REITs & property</span>
            </button>

            <button
              className="classify-btn crypto"
              onClick={() => handleClassify("Crypto")}
            >
              <span className="classify-icon">₿</span>
              <span className="classify-label">Crypto</span>
              <span className="classify-desc">Digital assets</span>
            </button>

            <button
              className="classify-btn metals"
              onClick={() => handleClassify("Precious Metals")}
            >
              <span className="classify-icon">🥇</span>
              <span className="classify-label">Precious Metals</span>
              <span className="classify-desc">Gold, silver, etc.</span>
            </button>

            <button
              className="classify-btn cash"
              onClick={() => handleClassify("Cash")}
            >
              <span className="classify-icon">💵</span>
              <span className="classify-label">Cash</span>
              <span className="classify-desc">Money market</span>
            </button>
          </div>

          <button className="skip-btn" onClick={handleSkip}>
            Not sure? Skip (will be marked as "Other")
          </button>
        </div>
      </div>
    );
  };

  const MonteCarloView = () => {
    const totalValue = getCurrentPortfolio().reduce(
      (sum, h) => sum + h.marketValue,
      0
    );
    const [mcYears, setMcYears] = useState(30);
    const [mcSimulations] = useState(1000);
    const [allYearResults, setAllYearResults] = useState(null); // Store all year results
    const [isSimulating, setIsSimulating] = useState(false);

    // Run simulation only once when component mounts or portfolio changes
    React.useEffect(() => {
      if (totalValue > 0 && !allYearResults) {
        setIsSimulating(true);
        // Run simulation for up to 40 years
        const results = runMonteCarloSimulation(totalValue, 40, mcSimulations);
        setAllYearResults(results);
        setIsSimulating(false);
      }
    }, [totalValue]);

    // Get results for currently selected year
    const mcResults = allYearResults ? allYearResults.getStatsForYear(mcYears) : null;

    return (
      <div className="montecarlo-container">
        <div className="nav-header">
          <h2>Monte Carlo Simulation</h2>
          <div className="nav-buttons">
            {user && (
              <UserProfile
                user={user}
                userData={userData}
                onUpgrade={() => setShowUpgrade(true)}
                onLogout={() => {
                  setPortfolios([]);
                  setPortfolio([]);
                  setCurrentPortfolioId(null);
                  setPortfolioName("");
                  setShowAggregated(false);
                  setView("upload");
                }}
                portfolios={portfolios}
                customAssetMappings={customAssetMappings}
                rebalanceThreshold={rebalanceThreshold}
                customAllocation={customAllocation}
                targetAllocation={targetAllocation}
              />
            )}
            <button
              onClick={() => setView("portfolio")}
              className="btn-secondary"
            >
              Back to Portfolio
            </button>
          </div>
        </div>

        <div className="mc-intro">
          <h3>🎲 What Are Your Chances?</h3>
          <p>
            Based on your current allocation and historical market data, we run
            1,000 simulations to show the range of possible outcomes for your
            portfolio.
          </p>
        </div>

        <div className="mc-controls">
          <div className="mc-input-group">
            <label>Time Horizon (Years)</label>
            <input
              type="range"
              min="5"
              max="40"
              value={mcYears}
              onChange={(e) => setMcYears(parseInt(e.target.value))}
            />
            <span className="mc-years-display">{mcYears} years</span>
          </div>
          <div className="mc-starting-value">
            <div className="mc-label">Starting Portfolio Value</div>
            <div className="mc-value">${totalValue.toLocaleString()}</div>
          </div>
          {allYearResults && (
            <button
              className="mc-rerun-btn"
              onClick={() => {
                setAllYearResults(null);
                setIsSimulating(true);
                setTimeout(() => {
                  const results = runMonteCarloSimulation(totalValue, 40, mcSimulations);
                  setAllYearResults(results);
                  setIsSimulating(false);
                }, 10);
              }}
            >
              🔄 Re-run Simulation
            </button>
          )}
        </div>

        {isSimulating && (
          <div className="mc-loading">
            <div className="loading-spinner">⏳</div>
            <p>Running 1,000 simulations across 40 years...</p>
          </div>
        )}

        {mcResults && !isSimulating && (
          <>
            <div className="mc-results-grid">
              <div className="mc-result-card best">
                <div className="mc-result-label">Best Case (90th %ile)</div>
                <div className="mc-result-value">
                  $
                  {mcResults.percentile90.toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="mc-result-note">Top 10% of outcomes</div>
              </div>

              <div className="mc-result-card good">
                <div className="mc-result-label">Good Case (75th %ile)</div>
                <div className="mc-result-value">
                  $
                  {mcResults.percentile75.toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="mc-result-note">Better than average</div>
              </div>

              <div className="mc-result-card median">
                <div className="mc-result-label">Expected (Median)</div>
                <div className="mc-result-value">
                  $
                  {mcResults.median.toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="mc-result-note">Most likely outcome</div>
              </div>

              <div className="mc-result-card poor">
                <div className="mc-result-label">Poor Case (25th %ile)</div>
                <div className="mc-result-value">
                  $
                  {mcResults.percentile25.toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="mc-result-note">Below average</div>
              </div>

              <div className="mc-result-card worst">
                <div className="mc-result-label">Worst Case (10th %ile)</div>
                <div className="mc-result-value">
                  $
                  {mcResults.percentile10.toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="mc-result-note">Bottom 10% of outcomes</div>
              </div>
            </div>

            <div className="mc-probability-section">
              <h3>Probability of Reaching Your Goals</h3>
              <div className="mc-goal-checks">
                <div className="mc-goal-item">
                  <div className="mc-goal-label">
                    Double Your Money (${(totalValue * 2).toLocaleString()})
                  </div>
                  <div className="mc-probability-bar">
                    <div
                      className="mc-probability-fill success"
                      style={{
                        width: `${
                          (mcResults.allResults.filter(
                            (r) => r >= totalValue * 2
                          ).length /
                            mcSimulations) *
                          100
                        }%`,
                      }}
                    >
                      {(
                        (mcResults.allResults.filter((r) => r >= totalValue * 2)
                          .length /
                          mcSimulations) *
                        100
                      ).toFixed(0)}
                      %
                    </div>
                  </div>
                </div>

                <div className="mc-goal-item">
                  <div className="mc-goal-label">
                    Triple Your Money (${(totalValue * 3).toLocaleString()})
                  </div>
                  <div className="mc-probability-bar">
                    <div
                      className="mc-probability-fill good"
                      style={{
                        width: `${
                          (mcResults.allResults.filter(
                            (r) => r >= totalValue * 3
                          ).length /
                            mcSimulations) *
                          100
                        }%`,
                      }}
                    >
                      {(
                        (mcResults.allResults.filter((r) => r >= totalValue * 3)
                          .length /
                          mcSimulations) *
                        100
                      ).toFixed(0)}
                      %
                    </div>
                  </div>
                </div>

                <div className="mc-goal-item">
                  <div className="mc-goal-label">Reach $1 Million</div>
                  <div className="mc-probability-bar">
                    <div
                      className="mc-probability-fill warning"
                      style={{
                        width: `${
                          (mcResults.allResults.filter((r) => r >= 1000000)
                            .length /
                            mcSimulations) *
                          100
                        }%`,
                      }}
                    >
                      {(
                        (mcResults.allResults.filter((r) => r >= 1000000)
                          .length /
                          mcSimulations) *
                        100
                      ).toFixed(0)}
                      %
                    </div>
                  </div>
                </div>

                <div className="mc-goal-item">
                  <div className="mc-goal-label">
                    Lose Money (Below ${totalValue.toLocaleString()})
                  </div>
                  <div className="mc-probability-bar">
                    <div
                      className="mc-probability-fill danger"
                      style={{
                        width: `${
                          (mcResults.allResults.filter((r) => r < totalValue)
                            .length /
                            mcSimulations) *
                          100
                        }%`,
                      }}
                    >
                      {(
                        (mcResults.allResults.filter((r) => r < totalValue)
                          .length /
                          mcSimulations) *
                        100
                      ).toFixed(0)}
                      %
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mc-disclaimers">
              <h4>⚠️ Important Disclaimers</h4>
              <ul>
                <li>
                  Based on historical returns - past performance doesn't
                  guarantee future results
                </li>
                <li>
                  Doesn't account for: contributions, withdrawals, taxes, fees,
                  or inflation
                </li>
                <li>
                  Assumes you maintain the same allocation throughout the period
                </li>
                <li>
                  Real markets have fat tails - extreme events happen more than
                  normal distributions predict
                </li>
                <li>This is educational only - not financial advice</li>
              </ul>
            </div>
          </>
        )}
      </div>
    );
  };

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#f1f5f9",
          fontSize: "1.5rem",
          fontFamily: "Inter, sans-serif",
        }}
      >
        🔐 Loading PortfolioRebalancer.io...
      </div>
    );
  }

  // Debug panel (remove this later)
  return (
    <>
      {/* Auth Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToSignup={() => {
          setShowLogin(false);
          setShowSignup(true);
        }}
      />

      <SignUpModal
        isOpen={showSignup}
        onClose={() => setShowSignup(false)}
        onSwitchToLogin={() => {
          setShowSignup(false);
          setShowLogin(true);
        }}
      />

      <UpgradePrompt
        isOpen={showUpgrade}
        user={user}
        onClose={() => {
          setShowUpgrade(false);
        }}
      />

      {/* Email Verification Banner */}
      {user && !user.emailVerified && (
        <div className="verification-banner">
          <div className="verification-banner-content">
            <span className="verification-banner-icon">⚠️</span>
            <div className="verification-banner-text">
              <strong>Please verify your email address</strong>
              <p>
                Check your inbox for a verification link. You'll need to verify
                to save portfolios.
              </p>
            </div>
            <button
              className="verification-banner-btn"
              onClick={async () => {
                const { error } = await resendVerificationEmail();
                if (!error) {
                  alert("Verification email sent! Please check your inbox.");
                  // Check if verified after sending (in case they already clicked the link)
                  await user.reload();
                  if (user.emailVerified) {
                    setUser({ ...user });
                  }
                } else {
                  alert("Error sending email: " + error);
                }
              }}
            >
              Resend Email
            </button>
          </div>
        </div>
      )}

      {/* Classify Tickers Modal */}
      <ClassifyTickersModal />

      <style>{`
        ${authStyles}
        
        /* Email Verification Banner */
        .verification-banner {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          padding: 16px;
          z-index: 9999;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .verification-banner-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .verification-banner-icon {
          font-size: 1.5rem;
        }

        .verification-banner-text {
          flex: 1;
          min-width: 200px;
        }

        .verification-banner-text strong {
          display: block;
          margin-bottom: 4px;
          font-size: 1.1rem;
        }

        .verification-banner-text p {
          font-size: 0.875rem;
          opacity: 0.95;
        }

        .verification-banner-btn {
          background: white;
          color: #d97706;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .verification-banner-btn:hover {
          background: #fef3c7;
          transform: translateY(-1px);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          min-height: 100vh;
          overflow-x: hidden; /* Prevent horizontal scroll */
        }

        * {
          box-sizing: border-box; /* Ensure padding doesn't cause overflow */
        }

        .upload-container, .portfolio-container, .rebalance-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
          width: 100%; /* Ensure it doesn't exceed viewport */
          overflow-x: hidden; /* Prevent horizontal scroll */
        }

        .upload-header {
          text-align: center;
          margin-bottom: 60px;
          position: relative;
          padding-top: 60px; /* Space for buttons/profile at top */
          min-height: 100px; /* Ensure enough vertical space */
        }

        .back-button {
          position: absolute;
          top: 0;
          left: 0;
          background: rgba(59, 130, 246, 0.1);
          border: 2px solid #3b82f6;
          color: #3b82f6;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: translateX(-2px);
        }

        .auth-buttons {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        /* Header right container - handles positioning for both auth buttons and profile */
        .header-right {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 10;
        }

        .signin-button-small {
          background: transparent;
          border: 2px solid #3b82f6;
          color: #3b82f6;
          padding: 10px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .signin-button-small:hover {
          background: rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
        }

        .signup-button-large {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          border: none;
          color: white;
          padding: 12px 28px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .signup-button-large:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }

        /* Classify Tickers Modal */
        .classify-modal {
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          border-radius: 16px;
          padding: 40px;
          max-width: 600px;
          width: 90%;
          border: 1px solid rgba(71, 85, 105, 0.5);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .classify-modal h2 {
          color: #f1f5f9;
          margin-bottom: 8px;
          font-size: 1.75rem;
          text-align: center;
        }

        .classify-subtitle {
          color: #94a3b8;
          margin-bottom: 24px;
          text-align: center;
        }

        .classify-progress {
          margin-bottom: 32px;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: rgba(51, 65, 85, 0.5);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%);
          transition: width 0.3s ease;
        }

        .progress-text {
          color: #94a3b8;
          font-size: 0.875rem;
          display: block;
          text-align: center;
        }

        .classify-ticker {
          text-align: center;
          margin-bottom: 24px;
        }

        .ticker-badge {
          display: inline-block;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
          padding: 12px 24px;
          border-radius: 8px;
          margin-bottom: 12px;
          letter-spacing: 1px;
        }

        .classify-question {
          color: #cbd5e1;
          font-size: 1.1rem;
        }

        .classify-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          margin-bottom: 24px;
        }

        .classify-btn {
          background: rgba(30, 41, 59, 0.6);
          border: 2px solid #334155;
          border-radius: 12px;
          padding: 16px 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .classify-btn:hover {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
        }

        .classify-icon {
          font-size: 2rem;
        }

        .classify-label {
          color: #f1f5f9;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .classify-desc {
          color: #64748b;
          font-size: 0.75rem;
        }

        .skip-btn {
          width: 100%;
          padding: 12px;
          background: transparent;
          border: 1px solid #475569;
          border-radius: 8px;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .skip-btn:hover {
          border-color: #64748b;
          background: rgba(51, 65, 85, 0.3);
        }

        .upload-mode-selector {
          background: rgba(30, 41, 59, 0.5);
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 40px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .upload-mode-selector h3 {
          color: #f1f5f9;
          margin-bottom: 16px;
          text-align: center;
        }

        .mode-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .mode-btn {
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid #334155;
          border-radius: 12px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
        }

        .mode-btn:hover {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
        }

        .mode-btn.active {
          border-color: #3b82f6;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
        }

        .mode-icon {
          font-size: 2.5rem;
          margin-bottom: 12px;
        }

        .mode-label {
          font-weight: 700;
          font-size: 1.1rem;
          color: #f1f5f9;
          margin-bottom: 8px;
        }

        .mode-desc {
          font-size: 0.875rem;
          color: #94a3b8;
          line-height: 1.4;
        }

        .append-mode-toggle {
          margin-top: 24px;
          padding: 20px;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 8px;
          border: 1px solid #334155;
        }

        .append-checkbox {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          font-size: 1rem;
        }

        .append-checkbox input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #3b82f6;
        }

        .append-label {
          color: #f1f5f9;
          user-select: none;
        }

        .append-hint {
          margin-top: 12px;
          padding: 12px;
          background: rgba(59, 130, 246, 0.1);
          border-left: 3px solid #3b82f6;
          border-radius: 4px;
          font-size: 0.875rem;
          color: #cbd5e1;
          line-height: 1.5;
        }

        .upload-hint {
          margin-top: 12px;
          color: #fbbf24;
          font-size: 0.9rem;
        }

        .upload-hint strong {
          color: #f59e0b;
        }

        h1 {
          font-size: 3rem;
          font-weight: 800;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .subtitle {
          font-size: 1.125rem;
          color: #94a3b8;
          font-weight: 400;
        }

        .upload-zone {
          background: rgba(30, 41, 59, 0.5);
          border: 2px dashed #475569;
          border-radius: 16px;
          padding: 80px 40px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          backdrop-filter: blur(10px);
        }

        .upload-zone:hover {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
          transform: translateY(-2px);
        }

        .upload-icon {
          width: 64px;
          height: 64px;
          color: #3b82f6;
          margin-bottom: 20px;
        }

        .upload-zone h3 {
          font-size: 1.5rem;
          margin-bottom: 8px;
          color: #f1f5f9;
        }

        .upload-zone p {
          color: #94a3b8;
          margin-bottom: 24px;
        }

        #file-upload {
          display: none;
        }

        .upload-button {
          display: inline-block;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          padding: 14px 32px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
        }

        .upload-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(59, 130, 246, 0.4);
        }

        .upload-divider {
          margin: 24px 0;
          text-align: center;
          position: relative;
        }

        .upload-divider::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #334155;
        }

        .upload-divider span {
          background: #0f172a;
          padding: 0 16px;
          position: relative;
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .manual-upload-btn {
          display: inline-block;
          background: rgba(139, 92, 246, 0.1);
          color: #a78bfa;
          padding: 14px 32px;
          border: 2px solid #8b5cf6;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        .manual-upload-btn:hover {
          background: rgba(139, 92, 246, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(139, 92, 246, 0.3);
        }

        /* Manual Entry Table */
        .manual-entry-container {
          background: rgba(30, 41, 59, 0.5);
          padding: 32px;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .manual-entry-container h3 {
          color: #f1f5f9;
          margin-bottom: 8px;
          font-size: 1.5rem;
        }

        .manual-entry-subtitle {
          color: #94a3b8;
          margin-bottom: 24px;
        }

        .manual-entry-table {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .manual-table-header {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr 1.2fr 1.5fr 60px;
          gap: 12px;
          padding: 12px 8px;
          font-weight: 600;
          color: #94a3b8;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #334155;
          margin-bottom: 12px;
        }

        .manual-table-row {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr 1.2fr 1.5fr 60px;
          gap: 12px;
          padding: 12px 8px;
          align-items: center;
          border-bottom: 1px solid rgba(51, 65, 85, 0.3);
        }

        .manual-table-row:last-child {
          border-bottom: none;
        }

        .manual-input {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 10px;
          color: #f1f5f9;
          font-size: 0.95rem;
          outline: none;
        }

        .manual-input:focus {
          border-color: #3b82f6;
        }

        .manual-select {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 10px;
          color: #f1f5f9;
          font-size: 0.95rem;
          outline: none;
          cursor: pointer;
        }

        .manual-select:focus {
          border-color: #3b82f6;
        }

        .manual-value {
          font-weight: 600;
          color: #10b981;
        }

        .remove-row-btn {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid #ef4444;
          border-radius: 6px;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
        }

        .remove-row-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: scale(1.1);
        }

        .manual-entry-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .add-row-btn {
          background: rgba(59, 130, 246, 0.1);
          border: 2px dashed #3b82f6;
          border-radius: 8px;
          padding: 12px 24px;
          color: #3b82f6;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-row-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          border-style: solid;
        }

        .manual-entry-buttons {
          display: flex;
          gap: 12px;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid #ef4444;
          color: #fca5a5;
          padding: 16px;
          border-radius: 8px;
          margin-top: 20px;
          text-align: center;
        }

        .csv-help {
          margin-top: 60px;
          padding: 32px;
          background: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }

        .csv-help h3 {
          margin-bottom: 16px;
          color: #f1f5f9;
        }

        .csv-help ul {
          list-style: none;
          margin: 16px 0;
        }

        .csv-help li {
          padding: 8px 0;
          padding-left: 24px;
          position: relative;
          color: #cbd5e1;
        }

        .csv-help li:before {
          content: "•";
          position: absolute;
          left: 0;
          color: #3b82f6;
          font-weight: bold;
        }

        .help-note {
          margin-top: 16px;
          font-size: 0.875rem;
          color: #94a3b8;
          font-style: italic;
        }

        .nav-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 40px;
        }

        .nav-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #f1f5f9;
        }

        .nav-buttons {
          display: flex;
          gap: 12px;
        }

        .btn-primary, .btn-secondary {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }

        .btn-secondary {
          background: rgba(30, 41, 59, 0.8);
          color: #e2e8f0;
          border: 1px solid #475569;
        }

        .btn-secondary:hover {
          background: rgba(51, 65, 85, 0.8);
          border-color: #64748b;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: rgba(30, 41, 59, 0.5);
          padding: 24px;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .stat-label {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #f1f5f9;
        }

        .content-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
        }

        .chart-section, .holdings-section, .actions-section {
          background: rgba(30, 41, 59, 0.5);
          padding: 32px;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .chart-section h3, .holdings-section h3, .actions-section h3 {
          margin-bottom: 24px;
          color: #f1f5f9;
          font-size: 1.25rem;
        }

        /* Pie chart legend styling */
        .chart-section .recharts-legend-wrapper {
          padding-top: 20px !important;
        }

        .chart-section .recharts-legend-item {
          margin-right: 16px !important;
          margin-bottom: 8px !important;
        }

        .chart-section .recharts-legend-item-text {
          color: #cbd5e1 !important;
          font-size: 0.875rem !important;
        }

        .actions-disclaimer {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-top: -16px;
          margin-bottom: 20px;
          font-style: italic;
        }

        .holdings-table {
          position: relative;
        }

        .holdings-scroll-container {
          overflow-y: auto;
          overflow-x: auto;
          max-height: 400px;
          -webkit-overflow-scrolling: touch;
        }

        .holdings-scroll-container .table-row {
          border-bottom: 1px solid rgba(51, 65, 85, 0.5);
          transition: background 0.2s ease;
        }

        .holdings-scroll-container .table-row:hover {
          background: rgba(51, 65, 85, 0.3);
        }

        .table-header, .table-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1.2fr 1.2fr 100px;
          gap: 16px;
          padding: 12px 8px;
          align-items: center;
        }

        .table-header {
          font-weight: 600;
          color: #94a3b8;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #334155;
          background: rgba(30, 41, 59, 0.95);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .symbol {
          font-weight: 600;
          color: #3b82f6;
        }

        .value {
          font-weight: 600;
        }

        .badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* Add/Delete Holding Buttons */
        .add-holding-btn {
          width: 100%;
          padding: 12px;
          background: rgba(59, 130, 246, 0.1);
          border: 2px dashed #3b82f6;
          border-radius: 8px;
          color: #3b82f6;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          margin-top: 8px;
        }

        .add-holding-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          border-style: solid;
        }

        .delete-holding-btn {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid #ef4444;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
        }

        .delete-holding-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: scale(1.1);
        }

        .edit-holding-btn {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid #3b82f6;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
        }

        .edit-holding-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: scale(1.1);
        }

        /* Edit mode styles */
        .table-row.edit-mode {
          background: rgba(59, 130, 246, 0.1);
          border: 2px solid #3b82f6;
          border-radius: 8px;
        }

        .edit-input {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #3b82f6;
          border-radius: 6px;
          padding: 8px;
          color: #f1f5f9;
          font-size: 0.95rem;
          outline: none;
        }

        .edit-input:focus {
          border-color: #60a5fa;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .edit-select {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #3b82f6;
          border-radius: 6px;
          padding: 8px;
          color: #f1f5f9;
          font-size: 0.95rem;
          outline: none;
          cursor: pointer;
        }

        .edit-select:focus {
          border-color: #60a5fa;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .save-edit-btn {
          background: rgba(16, 185, 129, 0.2);
          border: 1px solid #10b981;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
          color: #10b981;
        }

        .save-edit-btn:hover {
          background: rgba(16, 185, 129, 0.3);
          transform: scale(1.1);
        }

        .cancel-edit-btn {
          background: rgba(100, 116, 139, 0.2);
          border: 1px solid #64748b;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
          color: #94a3b8;
        }

        .cancel-edit-btn:hover {
          background: rgba(100, 116, 139, 0.3);
          transform: scale(1.1);
        }

        .save-holding-btn {
          background: rgba(16, 185, 129, 0.2);
          border: 1px solid #10b981;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          color: #10b981;
          font-weight: 700;
          transition: all 0.2s ease;
        }

        .save-holding-btn:hover {
          background: rgba(16, 185, 129, 0.3);
          transform: scale(1.1);
        }

        .cancel-holding-btn {
          background: rgba(100, 116, 139, 0.2);
          border: 1px solid #64748b;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          color: #94a3b8;
          font-weight: 700;
          transition: all 0.2s ease;
        }

        .cancel-holding-btn:hover {
          background: rgba(100, 116, 139, 0.3);
        }

        .add-holding-form {
          background: rgba(59, 130, 246, 0.05);
          border: 2px solid #3b82f6;
          border-radius: 8px;
          padding: 16px 8px !important;
        }

        .holding-input {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 8px;
          color: #f1f5f9;
          font-size: 0.9rem;
          outline: none;
        }

        .holding-input:focus {
          border-color: #3b82f6;
        }

        .holding-select {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 8px;
          color: #f1f5f9;
          font-size: 0.9rem;
          outline: none;
          cursor: pointer;
        }

        .holding-select:focus {
          border-color: #3b82f6;
        }

        .rebalance-settings {
          background: rgba(30, 41, 59, 0.5);
          padding: 32px;
          border-radius: 12px;
          margin-bottom: 30px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .rebalance-settings h3 {
          margin-bottom: 24px;
          color: #f1f5f9;
          font-size: 1.25rem;
        }

        .preset-selector {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          margin-bottom: 32px;
        }

        .preset-btn {
          background: rgba(15, 23, 42, 0.5);
          border: 2px solid #334155;
          border-radius: 12px;
          padding: 16px 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          color: #e2e8f0;
        }

        .preset-btn:hover {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
        }

        .preset-btn.active {
          border-color: #3b82f6;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
        }

        .preset-icon {
          font-size: 2rem;
          margin-bottom: 8px;
        }

        .preset-label {
          font-weight: 700;
          font-size: 0.95rem;
          margin-bottom: 4px;
          color: #f1f5f9;
        }

        .preset-desc {
          font-size: 0.75rem;
          color: #94a3b8;
          line-height: 1.2;
        }

        .allocation-inputs {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .allocation-total {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .allocation-total.success {
          background: rgba(16, 185, 129, 0.1);
          border: 2px solid #10b981;
        }

        .allocation-total.warning {
          background: rgba(251, 191, 36, 0.1);
          border: 2px solid #fbbf24;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        .total-label {
          color: #94a3b8;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .total-value {
          font-size: 1.5rem;
          color: #f1f5f9;
        }

        .allocation-total.warning .total-value {
          color: #fbbf24;
        }

        .allocation-total.success .total-value {
          color: #10b981;
        }

        .total-message {
          flex: 1;
          color: #fbbf24;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .allocation-input label {
          display: block;
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 8px;
          font-weight: 500;
          position: relative;
        }

        .tooltip-icon {
          display: inline-block;
          margin-left: 6px;
          color: #3b82f6;
          font-size: 0.875rem;
          cursor: help;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .tooltip-icon:hover {
          color: #60a5fa;
          transform: scale(1.1);
        }

        .allocation-input {
          position: relative;
        }

        .tooltip-text {
          display: none;
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          background: rgba(15, 23, 42, 0.98);
          border: 1px solid #3b82f6;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 8px;
          font-size: 0.8rem;
          line-height: 1.4;
          color: #cbd5e1;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          animation: tooltipFadeIn 0.2s ease;
        }

        .tooltip-text::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 20px;
          border: 6px solid transparent;
          border-top-color: #3b82f6;
        }

        .allocation-input:hover .tooltip-text {
          display: block;
        }

        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .input-group {
          display: flex;
          align-items: center;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid #334155;
          border-radius: 8px;
          overflow: hidden;
        }

        .input-group input {
          flex: 1;
          background: transparent;
          border: none;
          padding: 12px;
          color: #f1f5f9;
          font-size: 1rem;
          outline: none;
        }

        /* Use This Allocation Button */
        .use-preset-section {
          text-align: center;
          padding: 24px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
          border: 2px dashed rgba(59, 130, 246, 0.3);
          border-radius: 12px;
          margin-bottom: 24px;
        }

        .use-preset-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          border: none;
          padding: 14px 32px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .use-preset-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }

        .use-preset-hint {
          margin-top: 12px;
          color: #94a3b8;
          font-size: 0.875rem;
        }

        /* Read-only preset values */
        .preset-value {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(51, 65, 85, 0.3);
          border: 1px solid #334155;
          border-radius: 8px;
          padding: 12px 16px;
        }

        .preset-value .value-number {
          color: #94a3b8;
          font-size: 1rem;
          font-weight: 600;
        }

        .preset-value .locked-icon {
          opacity: 0.6;
          font-size: 0.875rem;
        }

        .input-group span {
          padding: 0 12px;
          color: #64748b;
          font-weight: 600;
        }

        .threshold-setting {
          max-width: 300px;
          position: relative;
        }

        .threshold-setting label {
          display: block;
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .threshold-setting input {
          width: 100%;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid #334155;
          border-radius: 8px;
          padding: 12px;
          color: #f1f5f9;
          font-size: 1rem;
          outline: none;
        }

        .threshold-tooltip {
          margin-top: 8px;
          font-size: 0.75rem;
          color: #64748b;
          line-height: 1.4;
          font-style: italic;
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
        }

        .allocation-bars {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .allocation-bar {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .bar-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .drift {
          color: #94a3b8;
        }

        .drift.alert {
          color: #fbbf24;
        }

        .bar-container {
          position: relative;
          height: 32px;
          background: rgba(15, 23, 42, 0.5);
          border-radius: 6px;
          overflow: visible;
        }

        .bar {
          height: 100%;
          border-radius: 6px;
          display: flex;
          align-items: center;
          padding: 0 12px;
          font-size: 0.875rem;
          font-weight: 600;
          color: white;
          transition: width 0.5s ease;
        }

        .bar-target {
          position: absolute;
          top: -4px;
          bottom: -4px;
          width: 3px;
          border-left: 3px dashed;
          opacity: 0.6;
        }

        .actions-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .action-card {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 8px;
          padding: 20px;
          border-left: 4px solid;
        }

        .action-card.buy {
          border-color: #10b981;
        }

        .action-card.sell {
          border-color: #ef4444;
        }

        .action-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .action-badge {
          background: rgba(255, 255, 255, 0.1);
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .action-card.buy .action-badge {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .action-card.sell .action-badge {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .drift-badge {
          font-size: 0.875rem;
          font-weight: 600;
          color: #fbbf24;
        }

        .action-body h4 {
          font-size: 1.125rem;
          margin-bottom: 12px;
          color: #f1f5f9;
        }

        .action-details {
          display: flex;
          gap: 24px;
          margin-bottom: 12px;
        }

        .detail {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail .label {
          font-size: 0.75rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail .value {
          font-size: 1rem;
          font-weight: 600;
          color: #f1f5f9;
        }

        .action-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-top: 8px;
        }

        .success-message {
          text-align: center;
          padding: 40px;
        }

        .success-message svg {
          width: 64px;
          height: 64px;
          color: #10b981;
          margin: 0 auto 20px;
        }

        .success-message p {
          font-size: 1.25rem;
          font-weight: 600;
          color: #f1f5f9;
          margin-bottom: 8px;
        }

        .success-message .sub {
          font-size: 1rem;
          color: #94a3b8;
          font-weight: 400;
        }

        .new-money-section {
          background: rgba(30, 41, 59, 0.5);
          padding: 32px;
          border-radius: 12px;
          margin-top: 30px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .new-money-header {
          margin-bottom: 24px;
        }

        .new-money-header h3 {
          color: #f1f5f9;
          font-size: 1.25rem;
          margin-bottom: 8px;
        }

        .new-money-subtitle {
          color: #94a3b8;
          font-size: 0.95rem;
        }

        .new-money-input-container {
          margin-bottom: 24px;
        }

        .new-money-input-container label {
          display: block;
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 12px;
          font-weight: 500;
        }

        .new-money-input-group {
          display: flex;
          align-items: center;
          background: rgba(15, 23, 42, 0.5);
          border: 2px solid #334155;
          border-radius: 8px;
          overflow: hidden;
          max-width: 300px;
          transition: border-color 0.3s ease;
        }

        .new-money-input-group:focus-within {
          border-color: #3b82f6;
        }

        .currency-symbol {
          padding: 0 16px;
          color: #64748b;
          font-weight: 600;
          font-size: 1.2rem;
        }

        .new-money-input-group input {
          flex: 1;
          background: transparent;
          border: none;
          padding: 14px 16px 14px 0;
          color: #f1f5f9;
          font-size: 1.1rem;
          font-weight: 600;
          outline: none;
        }

        .deployment-plan h4 {
          color: #f1f5f9;
          font-size: 1.1rem;
          margin-bottom: 20px;
        }

        .deployment-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
        }

        .deployment-item {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid #334155;
          border-radius: 8px;
          padding: 16px;
          transition: all 0.3s ease;
        }

        .deployment-item:hover {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
        }

        .deployment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .asset-badge {
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .deployment-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #10b981;
        }

        .deployment-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .deployment-bar-container {
          width: 100%;
          height: 8px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
          overflow: hidden;
        }

        .deployment-bar {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        .deployment-stats {
          display: flex;
          gap: 8px;
          align-items: center;
          font-size: 0.85rem;
        }

        .stat-item {
          color: #94a3b8;
        }

        .stat-item.new {
          color: #10b981;
          font-weight: 600;
        }

        .stat-item.target {
          color: #64748b;
          font-style: italic;
        }

        .stat-arrow {
          color: #3b82f6;
          font-weight: bold;
        }

        .unallocated-notice {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid #10b981;
          border-radius: 8px;
          padding: 16px;
          color: #10b981;
          margin-bottom: 24px;
          text-align: center;
        }

        .deployment-benefits {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .benefit-card {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 8px;
          padding: 16px;
          display: flex;
          gap: 12px;
        }

        .benefit-icon {
          font-size: 1.5rem;
        }

        .benefit-text strong {
          display: block;
          color: #f1f5f9;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }

        .benefit-text p {
          color: #94a3b8;
          font-size: 0.8rem;
          line-height: 1.4;
          margin: 0;
        }

        /* Portfolio Switcher */
        .portfolio-switcher {
          background: rgba(30, 41, 59, 0.5);
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 30px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .portfolio-switcher h3 {
          color: #f1f5f9;
          font-size: 1rem;
          margin-bottom: 12px;
        }

        .portfolio-tabs {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .portfolio-tab-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          gap: 4px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid #334155;
          border-radius: 8px;
          padding: 4px;
          transition: all 0.3s ease;
          max-width: 300px;
          min-width: 150px;
        }

        .portfolio-tab-wrapper:hover {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
        }

        .portfolio-tab-wrapper.active {
          border-color: #3b82f6;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
        }

        .portfolio-tab {
          background: transparent;
          border: none;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .portfolio-tab-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-items: flex-start;
        }

        .portfolio-tab-name {
          color: #f1f5f9;
          font-weight: 600;
          font-size: 0.95rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 200px;
        }

        .tab-value {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 600;
        }

        .portfolio-tab-actions {
          display: flex;
          gap: 4px;
          padding-right: 4px;
        }

        .tab-action-btn {
          background: rgba(30, 41, 59, 0.8);
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
        }

        .tab-action-btn:hover {
          background: rgba(51, 65, 85, 0.8);
          transform: scale(1.1);
        }

        .tab-action-btn.edit:hover {
          border-color: #3b82f6;
        }

        .tab-action-btn.delete:hover {
          border-color: #ef4444;
        }

        .portfolio-name-edit {
          flex: 1;
          padding: 8px 12px;
          max-width: 250px;
        }

        .portfolio-name-edit input {
          width: 100%;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid #3b82f6;
          border-radius: 6px;
          padding: 8px;
          color: #f1f5f9;
          font-size: 0.95rem;
          font-weight: 600;
          outline: none;
          max-width: 100%;
        }

        .portfolio-name-edit input::placeholder {
          color: #64748b;
          opacity: 0.7;
          font-style: italic;
          font-size: 0.875rem;
          font-weight: 400;
        }

        .portfolio-tab:hover {
          background: transparent;
        }

        .tab-value {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 600;
        }

        /* Diversification Score Analysis */
        .diversification-section {
          margin-top: 40px;
          background: rgba(30, 41, 59, 0.5);
          padding: 32px;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .diversification-section h3 {
          color: #f1f5f9;
          font-size: 1.25rem;
          margin-bottom: 24px;
        }

        .diversification-section h4 {
          color: #f1f5f9;
          font-size: 1.1rem;
          margin-bottom: 16px;
          margin-top: 32px;
        }

        .div-score-card {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-radius: 12px;
          padding: 32px;
          margin-bottom: 24px;
          display: flex;
          gap: 32px;
          align-items: center;
          flex-wrap: wrap;
        }

        .div-score-main {
          text-align: center;
          min-width: 200px;
        }

        .div-score-label {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .div-score-value {
          font-size: 4rem;
          font-weight: 800;
          margin-bottom: 8px;
          line-height: 1;
        }

        .div-score-value.excellent {
          color: #10b981;
        }

        .div-score-value.good {
          color: #3b82f6;
        }

        .div-score-value.moderate {
          color: #fbbf24;
        }

        .div-score-value.poor {
          color: #ef4444;
        }

        .div-score-rating {
          font-size: 1.25rem;
          font-weight: 600;
          color: #f1f5f9;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .div-score-explanation {
          flex: 1;
          min-width: 300px;
        }

        .div-explanation-text {
          color: #cbd5e1;
          font-size: 1rem;
          line-height: 1.6;
        }

        .div-explanation-text strong {
          color: #f1f5f9;
        }

        .div-metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .div-metric-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid #334155;
          border-radius: 10px;
          padding: 20px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .div-metric-icon {
          font-size: 2rem;
        }

        .div-metric-content {
          flex: 1;
        }

        .div-metric-label {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .div-metric-value {
          font-size: 2rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 4px;
        }

        .div-metric-note {
          color: #64748b;
          font-size: 0.8rem;
          line-height: 1.4;
        }

        .div-insights {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .div-insight {
          display: flex;
          gap: 16px;
          padding: 20px;
          border-radius: 10px;
          border-left: 4px solid;
        }

        .div-insight.excellent {
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
        }

        .div-insight.good {
          background: rgba(59, 130, 246, 0.1);
          border-color: #3b82f6;
        }

        .div-insight.moderate {
          background: rgba(251, 191, 36, 0.1);
          border-color: #fbbf24;
        }

        .div-insight.poor {
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
        }

        .div-insight.warning {
          background: rgba(251, 191, 36, 0.1);
          border-color: #fbbf24;
        }

        .div-insight.success {
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
        }

        .div-insight-icon {
          font-size: 1.5rem;
        }

        .div-insight-text strong {
          display: block;
          color: #f1f5f9;
          margin-bottom: 6px;
          font-size: 1rem;
        }

        .div-insight-text p {
          color: #cbd5e1;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        .div-reference {
          margin-top: 32px;
          background: rgba(15, 23, 42, 0.3);
          padding: 24px;
          border-radius: 10px;
        }

        .div-reference-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .div-ref-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 6px;
        }

        .div-ref-pair {
          color: #cbd5e1;
          font-size: 0.875rem;
        }

        .div-ref-value {
          font-weight: 700;
          font-size: 0.9rem;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .div-ref-value.high {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .div-ref-value.medium {
          background: rgba(251, 191, 36, 0.2);
          color: #fbbf24;
        }

        .div-ref-value.low {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .div-ref-note {
          color: #64748b;
          font-size: 0.8rem;
          font-style: italic;
          margin: 0;
        }

        /* Monte Carlo Simulation */
        .montecarlo-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .mc-intro {
          background: rgba(30, 41, 59, 0.5);
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 30px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .mc-intro h3 {
          color: #f1f5f9;
          margin-bottom: 12px;
        }

        .mc-intro p {
          color: #94a3b8;
          line-height: 1.6;
        }

        .mc-controls {
          background: rgba(30, 41, 59, 0.5);
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 30px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
          display: flex;
          gap: 40px;
          align-items: center;
          flex-wrap: wrap;
        }

        .mc-input-group {
          flex: 1;
          min-width: 300px;
        }

        .mc-input-group label {
          display: block;
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 12px;
          font-weight: 500;
        }

        .mc-input-group input[type="range"] {
          width: 100%;
          height: 8px;
          border-radius: 4px;
          background: rgba(71, 85, 105, 0.5);
          outline: none;
          -webkit-appearance: none;
        }

        .mc-input-group input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }

        .mc-years-display {
          display: inline-block;
          margin-top: 8px;
          font-size: 1.5rem;
          font-weight: 700;
          color: #3b82f6;
        }

        .mc-starting-value {
          text-align: center;
        }

        .mc-rerun-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mc-rerun-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }

        .mc-loading {
          background: rgba(30, 41, 59, 0.5);
          padding: 40px;
          border-radius: 12px;
          text-align: center;
          margin-bottom: 30px;
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .loading-spinner {
          font-size: 3rem;
          margin-bottom: 16px;
          animation: spin 2s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .mc-loading p {
          color: #94a3b8;
          font-size: 1rem;
        }

        .mc-label {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .mc-value {
          font-size: 2rem;
          font-weight: 700;
          color: #f1f5f9;
        }

        .mc-results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 40px;
        }

        .mc-result-card {
          padding: 20px;
          border-radius: 12px;
          border: 2px solid;
          backdrop-filter: blur(10px);
          transition: transform 0.3s ease;
        }

        .mc-result-card:hover {
          transform: translateY(-4px);
        }

        .mc-result-card.best {
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
        }

        .mc-result-card.good {
          background: rgba(52, 211, 153, 0.1);
          border-color: #34d399;
        }

        .mc-result-card.median {
          background: rgba(59, 130, 246, 0.1);
          border-color: #3b82f6;
        }

        .mc-result-card.poor {
          background: rgba(251, 191, 36, 0.1);
          border-color: #fbbf24;
        }

        .mc-result-card.worst {
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
        }

        .mc-result-label {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .mc-result-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 4px;
        }

        .mc-result-note {
          color: #64748b;
          font-size: 0.75rem;
          font-style: italic;
        }

        .mc-probability-section {
          background: rgba(30, 41, 59, 0.5);
          padding: 32px;
          border-radius: 12px;
          margin-bottom: 30px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(71, 85, 105, 0.5);
        }

        .mc-probability-section h3 {
          color: #f1f5f9;
          margin-bottom: 24px;
        }

        .mc-goal-checks {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .mc-goal-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mc-goal-label {
          color: #cbd5e1;
          font-weight: 500;
        }

        .mc-probability-bar {
          width: 100%;
          height: 36px;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 6px;
          overflow: hidden;
          position: relative;
        }

        .mc-probability-fill {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          color: white;
          transition: width 0.5s ease;
        }

        .mc-probability-fill.success {
          background: linear-gradient(90deg, #10b981, #34d399);
        }

        .mc-probability-fill.good {
          background: linear-gradient(90deg, #3b82f6, #60a5fa);
        }

        .mc-probability-fill.warning {
          background: linear-gradient(90deg, #f59e0b, #fbbf24);
        }

        .mc-probability-fill.danger {
          background: linear-gradient(90deg, #ef4444, #f87171);
        }

        .mc-disclaimers {
          background: rgba(251, 191, 36, 0.1);
          border: 1px solid #fbbf24;
          border-radius: 12px;
          padding: 24px;
        }

        .mc-disclaimers h4 {
          color: #fbbf24;
          margin-bottom: 16px;
        }

        .mc-disclaimers ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .mc-disclaimers li {
          color: #cbd5e1;
          padding: 6px 0;
          padding-left: 20px;
          position: relative;
          font-size: 0.875rem;
        }

        .mc-disclaimers li:before {
          content: "⚠️";
          position: absolute;
          left: 0;
        }

        @media (max-width: 768px) {
          /* Container and grid layouts - force single column */
          .content-grid, .comparison-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          /* Typography */
          h1 {
            font-size: 1.75rem;
            line-height: 1.2;
          }

          h2 {
            font-size: 1.5rem;
          }

          h3 {
            font-size: 1.25rem;
          }

          /* Upload header mobile */
          .upload-header {
            padding-top: 80px; /* More space for stacked buttons */
          }

          .header-right {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
          }

          .auth-buttons {
            flex-direction: column;
            gap: 8px;
          }

          .signin-button-small,
          .signup-button-large {
            width: 100%;
            text-align: center;
          }

          /* Chart sections - full width, reduce padding */
          .chart-section, .holdings-section, .actions-section {
            padding: 20px 12px;
            width: 100%;
            overflow-x: hidden; /* Prevent section overflow */
          }

          /* Pie chart - make responsive */
          .chart-section {
            min-height: 450px;
          }

          /* Holdings table wrapper - scroll container handles scrolling */
          .holdings-table {
            width: 100%;
          }

          /* Holdings scroll container */
          .holdings-scroll-container {
            overflow-x: auto;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            max-height: 400px;
          }

          /* Table header and rows - set minimum width to enable scroll */
          .table-header, .table-row {
            font-size: 0.7rem;
            gap: 8px;
            min-width: 650px; /* Force horizontal scroll */
            grid-template-columns: 80px 60px 70px 90px 100px 80px; /* Fixed widths */
            padding: 10px 6px;
          }

          .table-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: rgba(30, 41, 59, 0.98);
          }

          /* Action buttons in table */
          .table-row .action-buttons {
            flex-direction: row;
            gap: 4px;
            width: auto;
          }

          .table-row .action-buttons button {
            width: auto;
            padding: 6px 10px;
            font-size: 0.7rem;
          }

          /* Diversification section mobile fixes */
          .diversification-section {
            padding: 20px 12px;
            margin-top: 20px;
            overflow: hidden; /* Prevent overflow */
            word-wrap: break-word; /* Break long words */
          }

          .diversification-section h3 {
            font-size: 1.1rem;
            word-wrap: break-word;
          }

          .diversification-section h4 {
            font-size: 1rem;
            word-wrap: break-word;
          }

          .div-score-card {
            padding: 20px 12px;
            gap: 16px;
            flex-direction: column;
            align-items: stretch;
          }

          .div-score-main {
            min-width: auto;
            width: 100%;
          }

          .div-score-value {
            font-size: 3rem; /* Smaller on mobile */
          }

          .div-score-rating {
            font-size: 1rem;
          }

          .div-score-explanation {
            min-width: auto;
            width: 100%;
          }

          .div-explanation-text {
            font-size: 0.9rem;
            line-height: 1.6;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .div-metrics-grid {
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 12px;
          }

          .div-metric-card {
            padding: 16px 12px;
            word-wrap: break-word;
          }

          .div-metric-value {
            font-size: 1.5rem;
          }

          .div-metric-description {
            font-size: 0.85rem;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          /* Allocation bars - make scrollable if needed */
          .allocation-bars {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          /* Rebalancing table - horizontal scroll */
          .rebalancing-table {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            display: block;
          }

          /* Monte Carlo - single column */
          .mc-controls {
            flex-direction: column;
            gap: 16px;
          }

          .mc-rerun-btn {
            width: 100%;
            justify-content: center;
          }

          .monte-carlo-controls {
            flex-direction: column;
            gap: 12px;
          }

          .mc-control-group {
            width: 100%;
          }

          /* Upload mode selector */
          .mode-buttons {
            flex-direction: column;
            gap: 12px;
          }

          .mode-btn {
            width: 100%;
          }

          /* File upload card */
          .file-upload-card {
            padding: 24px 16px;
          }

          /* Manual entry */
          .manual-entry-form {
            grid-template-columns: 1fr;
          }

          /* Portfolio selector dropdown */
          .portfolio-selector {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .portfolio-selector select {
            width: 100%;
          }

          /* Nav buttons */
          .nav-buttons {
            flex-direction: column;
            gap: 8px;
            width: 100%;
          }

          .nav-buttons button {
            width: 100%;
          }

          /* Preset selector */
          .preset-selector {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .preset-btn {
            padding: 12px 16px;
          }

          /* Use preset button on mobile */
          .use-preset-section {
            padding: 16px 12px;
          }

          .use-preset-btn {
            width: 100%;
            justify-content: center;
            padding: 12px 24px;
            font-size: 0.95rem;
          }

          .use-preset-hint {
            font-size: 0.8rem;
          }

          /* Preset values on mobile */
          .preset-value {
            padding: 10px 12px;
          }

          .preset-value .value-number {
            font-size: 0.95rem;
          }

          /* Target allocation adjusters */
          .allocation-adjuster {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          /* Action buttons - stack vertically (except in table) */
          .actions-section .action-buttons {
            flex-direction: column;
            gap: 8px;
          }

          .actions-section .action-buttons button {
            width: 100%;
          }

          /* Remove horizontal margins on mobile */
          .upload-container,
          .portfolio-container,
          .rebalance-container,
          .monte-carlo-container {
            padding: 16px 12px;
            margin: 0;
            width: 100%;
            overflow-x: hidden;
          }

          /* Modals on mobile */
          .classify-modal {
            max-width: 95%;
            max-height: 90vh;
            overflow-y: auto;
            padding: 20px 16px;
          }

          /* User profile dropdown on mobile */
          .profile-dropdown {
            right: 0;
            left: auto;
            min-width: 200px;
          }

          /* Portfolio tabs - make scrollable horizontally if many portfolios */
          .portfolio-tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            flex-wrap: nowrap;
            padding-bottom: 8px;
          }

          .portfolio-tab {
            min-width: 150px;
          }
        }
      `}</style>

      <div>
        {view === "upload" && <UploadView />}
        {view === "portfolio" && <PortfolioView />}
        {view === "rebalance" && <RebalanceView />}
        {view === "montecarlo" && <MonteCarloView />}
      </div>
    </>
  );
};

export default RebalancingAssistant;
